/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.9.1
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
import _Symbol from 'babel-runtime/core-js/symbol';
import Sphere from 'ol/sphere';
import _typeof from 'babel-runtime/helpers/typeof';
import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import { Observable as Observable$1 } from 'rxjs/Observable';
import { merge as merge$1 } from 'rxjs/observable/merge';
import { fromEventPattern as fromEventPattern$1 } from 'rxjs/observable/fromEventPattern';
import _isFunction from 'lodash/fp/isFunction';
import _isEqual from 'lodash/fp/isEqual';
import { distinctUntilChanged as distinctUntilChanged$1 } from 'rxjs/operator/distinctUntilChanged';
import { map as map$1 } from 'rxjs/operator/map';
import { throttleTime as throttleTime$1 } from 'rxjs/operator/throttleTime';
import olextent from 'ol/extent';
import olproj from 'ol/proj';
import _isPlainObject from 'lodash/fp/isPlainObject';
import _get from 'lodash/fp/get';
import Feature from 'ol/feature';
import GeoJSON from 'ol/format/geojson';
import TopoJSON from 'ol/format/topojson';
import turfPointOnSurface from '@turf/point-on-surface';
import GeometryCollection from 'ol/geom/geometrycollection';
import LineString from 'ol/geom/linestring';
import MultiLineString from 'ol/geom/multilinestring';
import MultiPoint from 'ol/geom/multipoint';
import MultiPolygon from 'ol/geom/multipolygon';
import Point from 'ol/geom/point';
import Polygon from 'ol/geom/polygon';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import Projection from 'ol/proj/projection';
import _Object$assign from 'babel-runtime/core-js/object/assign';
import _extends from 'babel-runtime/helpers/extends';
import _Object$keys from 'babel-runtime/core-js/object/keys';
import _upperFirst from 'lodash/fp/upperFirst';
import _reduce from 'lodash/fp/reduce';
import _pick from 'lodash/fp/pick';
import _lowerFirst from 'lodash/fp/lowerFirst';
import _flow from 'lodash/fp/flow';
import parseColor from 'parse-color';
import Style from 'ol/style/style';
import Fill from 'ol/style/fill';
import Stroke from 'ol/style/stroke';
import Circle from 'ol/style/circle';
import Icon from 'ol/style/icon';
import RegularShape from 'ol/style/regularshape';
import Text from 'ol/style/text';
import ImageStyle from 'ol/style/image';
import olsize from 'ol/size';
import oltilegrid from 'ol/tilegrid';
import TileGrid from 'ol/tilegrid/tilegrid';
import loadingstrategy from 'ol/loadingstrategy';
import _isNumber from 'lodash/fp/isNumber';
import _isString from 'lodash/fp/isString';
import Vue from 'vue';
import easing from 'ol/easing';
import _Object$values from 'babel-runtime/core-js/object/values';
import _Object$create from 'babel-runtime/core-js/object/create';
import uuid from 'uuid/v4';
import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import _getOr from 'lodash/fp/getOr';
import _Object$defineProperties from 'babel-runtime/core-js/object/define-properties';
import mergeDescriptors from 'merge-descriptors';
import _regeneratorRuntime from 'babel-runtime/regenerator';
import _Promise from 'babel-runtime/core-js/promise';
import _asyncToGenerator from 'babel-runtime/helpers/asyncToGenerator';
import { interval as interval$1 } from 'rxjs/observable/interval';
import { first as first$1 } from 'rxjs/operator/first';
import { skipWhile as skipWhile$1 } from 'rxjs/operator/skipWhile';
import { toPromise as toPromise$1 } from 'rxjs/operator/toPromise';
import _identity from 'lodash/fp/identity';
import _createClass from 'babel-runtime/helpers/createClass';
import Interaction from 'ol/interaction/interaction';
import Layer from 'ol/layer/layer';
import Overlay from 'ol/overlay';
import _map from 'lodash/fp/map';
import _filter from 'lodash/fp/filter';
import _constant from 'lodash/fp/constant';
import { _do as _do$1 } from 'rxjs/operator/do';
import { debounceTime as debounceTime$1 } from 'rxjs/operator/debounceTime';
import { createTileUrlFunction } from 'ol-tilecache';
import XYZSource from 'ol/source/xyz';
import _pickBy from 'lodash/fp/pickBy';
import _isBoolean from 'lodash/fp/isBoolean';

/**
 * @module core/consts
 */
/**
 * @const {Symbol<string>}
 */
var SERVICES_PROP = _Symbol('services');
/**
 * @const {string}
 */
var VM_PROP = 'vm';
/**
 * @const {Symbol<string>}
 */
var IDENTITY_MAP_PROP = _Symbol('identityMap');

/**
 * VueLayers global options key.
 * @type {symbol}
 */
var VL_OPTIONS = _Symbol('options');

var EPSG_4326 = 'EPSG:4326';
var EPSG_3857 = 'EPSG:3857';
/**
 * @type {number} Default map max zoom
 */
var MAX_ZOOM = 28;
/**
 * @type {number} Default map min zoom
 */
var MIN_ZOOM = 0;
/**
 * @type {number} Default tile size
 */
var TILE_SIZE = 256;
/**
 * @type {string}
 */
var TILE_FORMAT = 'png';
/**
 * @type {number} Default zoom factor
 */
var ZOOM_FACTOR = 2;
/**
 * @type {number}
 */
var CACHE_SIZE = 2048;
/**
 * @type {number}
 */
var PIXEL_RATIO = 1;
/**
 * @type {string}
 */
var CROSS_ORIGIN = 'anonymous';
/**
 * @type {number}
 */
var REPROJ_ERR_THRESHOLD = 0.5;
/**
 * @type {number} Earth radius in meters
 */
var EARTH_RADIUS = 6378137;
var WGS84_SPHERE = new Sphere(EARTH_RADIUS);

var RENDERER_TYPE = {
  CANVAS: 'canvas',
  WEBGL: 'webgl'
};

var GEOMETRY_TYPE = {
  POINT: 'Point',
  LINE_STRING: 'LineString',
  POLYGON: 'Polygon',
  MULTI_POINT: 'MultiPoint',
  MULTI_LINE_STRING: 'MultiLineString',
  MULTI_POLYGON: 'MultiPolygon',
  GEOMETRY_COLLECTION: 'GeometryCollection',
  CIRCLE: 'Circle'
};

var EXTENT_CORNER = {
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_RIGHT: 'bottom-right',
  TOP_LEFT: 'top-left',
  TOP_RIGHT: 'top-right'
};

var PROJ_UNIT = {
  DEGREES: 'degrees',
  FEET: 'ft',
  METERS: 'm',
  PIXELS: 'pixels',
  TILE_PIXELS: 'tile-pixels',
  USFEET: 'us-ft'
};

var OVERLAY_POSITIONING = {
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_CENTER: 'bottom-center',
  BOTTOM_RIGHT: 'bottom-right',
  CENTER_LEFT: 'center-left',
  CENTER_CENTER: 'center-center',
  CENTER_RIGHT: 'center-right',
  TOP_LEFT: 'top-left',
  TOP_CENTER: 'top-center',
  TOP_RIGHT: 'top-right'

  /**
   * @type {string} Default WMS version
   */
};var WMS_VERSION = '1.3.0';

var WMTS_VERSION = '1.0.0';
var WMTS_REQUEST_ENCODING = 'KVP';
var WMTS_FORMAT = 'image/jpeg';
var LAYER_PROP = 'layer';

/**
 * Creates an Observable using OpenLayers event pattern that emits events coming from the given event target.
 *
 * @example **Subscribe on view center change events**
 * const map = ol.Map({ ... })
 * const changes = Observable.fromOlEvent(map.getView(), 'change:center')
 *
 * changes.subscribe(({ coordinate }) => console.log(coordinate))
 *
 * @param {ol.Object} target OpenLayers event target.
 * @param {string|Object[]} eventName The event name of interest, being emitted by the `target`
 *                          or an array of events/selectors like `[{ event: 'event1', selector?: x => x }, ...]`.
 * @param {function(...*): *} [selector] An optional function to post-process results. It takes the arguments
 *    from the event handler and should return a single value.
 * @return {Observable<T>}
 * @memberOf {Observable}
 */
function fromOlEvent(target, eventName, selector) {
  if (Array.isArray(eventName)) {
    return merge$1.call.apply(merge$1, [Observable$1].concat(_toConsumableArray(eventName.map(function (elem) {
      var eventName = void 0,
          selector = void 0;

      if ((typeof elem === 'undefined' ? 'undefined' : _typeof(elem)) === 'object') {
        eventName = elem.event;
        selector = elem.selector;
      } else {
        eventName = elem;
      }

      return fromOlEvent(target, eventName, selector);
    }))));
  }

  return fromEventPattern$1.call(Observable$1, function (handler) {
    return target.on(eventName, handler);
  }, function (handler) {
    return target.un(eventName, handler);
  }, selector);
}

/**
 * Creates Observable from OpenLayers change:* event
 * @param {ol.Object} target
 * @param {string|string[]} [prop]
 * @param {boolean|function(a, b):boolean|undefined} [distinct] Distinct values by lodash isEqual values or
 *                                                              by custom comparator
 * @param {number|undefined} [throttle] Throttle values by passed amount of ms.
 * @param {function(target: ol.Object, prop: string):*|undefined} [selector] Custom selector
 * @return {Observable<{prop: string, value: *}>}
 */
function fromOlChangeEvent(target, prop, distinct, throttle, selector) {
  var _context3;

  if (Array.isArray(prop)) {
    return merge$1.call.apply(merge$1, [Observable$1].concat(_toConsumableArray(prop.map(function (p) {
      return fromOlChangeEvent(target, p);
    }))));
  }

  selector = selector || function (target, prop) {
    return target.get(prop);
  };
  var event = 'change:' + prop;
  var observable = fromOlEvent(target, event, function () {
    return selector(target, prop);
  });

  if (throttle != null) {
    var _context;

    observable = (_context = observable, throttleTime$1).call(_context, throttle);
  }
  if (distinct) {
    var _context2;

    _isFunction(distinct) || (distinct = _isEqual);
    observable = (_context2 = observable, distinctUntilChanged$1).call(_context2, distinct);
  }

  return (_context3 = observable, map$1).call(_context3, function (value) {
    return { prop: prop, value: value };
  });
}

/**
 * Extent extensions
 */
var getWidth = olextent.getWidth;
var getHeight = olextent.getHeight;
var boundingExtent = olextent.boundingExtent;
var buffer = olextent.buffer;
var equals = olextent.equals;
var extend = olextent.extend;

function createOrUpdate(minX, minY, maxX, maxY, extent) {
  if (extent) {
    extent[0] = minX;
    extent[1] = minY;
    extent[2] = maxX;
    extent[3] = maxY;

    return extent;
  } else {
    return [minX, minY, maxX, maxY];
  }
}

/**
 * Get a corner coordinate of an extent.
 * @param {ol.Extent} extent Extent.
 * @param {ol.extent.Corner} corner Corner.
 * @return {ol.Coordinate} Corner coordinate.
 * @see https://github.com/openlayers/openlayers/blob/master/src/ol/extent.js#L482
 */
function getCorner(extent, corner) {
  var coordinate = void 0;
  if (corner === EXTENT_CORNER.BOTTOM_LEFT) {
    coordinate = olextent.getBottomLeft(extent);
  } else if (corner === EXTENT_CORNER.BOTTOM_RIGHT) {
    coordinate = olextent.getBottomRight(extent);
  } else if (corner === EXTENT_CORNER.TOP_LEFT) {
    coordinate = olextent.getTopLeft(extent);
  } else if (corner === EXTENT_CORNER.TOP_RIGHT) {
    coordinate = olextent.getTopRight(extent);
  } else {
    throw new Error('Invalid corner');
  }
  return coordinate;
}

/**
 * Generate a tile grid extent from a projection.  If the projection has an
 * extent, it is used.  If not, a global extent is assumed.
 * @param {ol.ProjectionLike} projection Projection.
 * @return {ol.Extent} Extent.
 * @see https://github.com/openlayers/openlayers/blob/master/src/ol/tilegrid.js#L148
 */
function fromProjection(projection) {
  projection = olproj.get(projection);
  var extent = projection.getExtent();

  if (!extent) {
    var half = 180 * olproj.METERS_PER_UNIT[PROJ_UNIT.DEGREES] / projection.getMetersPerUnit();
    extent = createOrUpdate(-half, -half, half, half);
  }
  return extent;
}

var extent = Object.freeze({
	getWidth: getWidth,
	getHeight: getHeight,
	boundingExtent: boundingExtent,
	buffer: buffer,
	equals: equals,
	extend: extend,
	createOrUpdate: createOrUpdate,
	getCorner: getCorner,
	fromProjection: fromProjection
});

/**
 * @param {olx.format.GeoJSONOptions} [options]
 * @return {ol.format.GeoJSON}
 */
function geoJson$1(options) {
  return new GeoJSON(options);
}

/**
 * @param {olx.format.TopoJSONOptions} [options]
 * @return {ol.format.TopoJSON}
 */
function topoJson(options) {
  return new TopoJSON(options);
}

var format = Object.freeze({
	geoJson: geoJson$1,
	topoJson: topoJson
});

var geoJson$$1 = geoJson$1();

/**
 * @param {ol.Feature} feature
 * @param {ol.ProjectionLike|undefined} [featureProjection]
 * @param {ol.ProjectionLike|undefined} [dataProjection]
 * @return {GeoJSONFeature|Object}
 */
function writeFeature(feature, featureProjection, dataProjection) {
  var geoJsonFeature = geoJson$$1.writeFeatureObject(feature, { featureProjection: featureProjection, dataProjection: dataProjection });

  if (Array.isArray(_get(geoJsonFeature, 'properties.features'))) {
    geoJsonFeature.properties.features = geoJsonFeature.properties.features.map(function (feature) {
      if (feature instanceof Feature) {
        return writeFeature(feature, featureProjection, dataProjection);
      }
      return feature;
    });
  }

  return geoJsonFeature;
}

/**
 * @param {GeoJSONFeature|Object} geoJsonFeature
 * @param {ol.ProjectionLike|undefined} [featureProjection]
 * @param {ol.ProjectionLike|undefined} [dataProjection]
 * @return {ol.Feature}
 */
function readFeature(geoJsonFeature, featureProjection, dataProjection) {
  var feature = geoJson$$1.readFeature(geoJsonFeature, { featureProjection: featureProjection, dataProjection: dataProjection });

  if (Array.isArray(feature.get('features'))) {
    feature.set('features', feature.get('features').map(function (feature) {
      if (_isPlainObject(feature)) {
        return readFeature(feature, featureProjection, dataProjection);
      }
      return feature;
    }));
  }

  return feature;
}

/**
 * @param {ol.geom.Geometry} geometry
 * @param {ol.ProjectionLike|undefined} [geometryProjection]
 * @param {ol.ProjectionLike|undefined} [dataProjection]
 * @return {GeoJSONGeometry|GeoJSONGeometryCollection|Object}
 */
function writeGeometry(geometry, geometryProjection, dataProjection) {
  return geoJson$$1.writeGeometryObject(geometry, {
    featureProjection: geometryProjection,
    dataProjection: dataProjection
  });
}

/**
 * @param {GeoJSONGeometry|Object} geoJsonGeometry
 * @param {ol.ProjectionLike|undefined} [geometryProjection]
 * @param {ol.ProjectionLike|undefined} [dataProjection]
 * @return {ol.geom.Geometry}
 */
function readGeometry(geoJsonGeometry, geometryProjection, dataProjection) {
  dataProjection = readProjection(geoJsonGeometry, dataProjection);

  return geoJson$$1.readGeometry(geoJsonGeometry, {
    featureProjection: geometryProjection,
    dataProjection: dataProjection
  });
}

function readProjection(geoJsonObj, defaultProjection) {
  return geoJson$$1.readProjection(geoJsonObj) || defaultProjection;
}

var geojson = Object.freeze({
	writeFeature: writeFeature,
	readFeature: readFeature,
	writeGeometry: writeGeometry,
	readGeometry: readGeometry,
	readProjection: readProjection
});

/**
 * @param {number|number[]} lonOrCoordinates
 * @param {number} [lat]
 * @return {ol.geom.Point}
 */
function point(lonOrCoordinates, lat) {
  var coordinates = Array.isArray(lonOrCoordinates) ? lonOrCoordinates : [lonOrCoordinates, lat];

  return new Point(coordinates);
}

/**
 * @param {Array<number[]>} points
 * @returns {ol.geom.LineString}
 */
function line(points) {
  return new LineString(points);
}

/**
 * @param {Array<Array<number[]>>} rings
 * @returns {ol.geom.Polygon}
 */
function polygon(rings) {
  return new Polygon(rings);
}

/**
 * @param {Array<number[]>} points
 * @returns {ol.geom.MultiPoint}
 */
function multiPoint(points) {
  return new MultiPoint(points);
}

/**
 * @param {Array<Array<number[]>>} lines
 * @returns {ol.geom.MultiLineString}
 */
function multiLine(lines) {
  return new MultiLineString(lines);
}

/**
 * @param {Array<Array<Array<number[]>>>} polygons
 * @returns {ol.geom.MultiPolygon}
 */
function multiPolygon(polygons) {
  return new MultiPolygon(polygons);
}

/**
 * @param {ol.geom.Geometry[]} geoms
 * @returns {ol.geom.GeometryCollection}
 */
function collection(geoms) {
  return new GeometryCollection(geoms);
}

/**
 * @param {ol.Coordinate|number[]} center
 * @param {number} radius
 * @return {ol.geom.Polygon}
 */
function circularPolygon(center, radius) {
  return Polygon.circular(WGS84_SPHERE, center, radius);
}

/**
 * @param {ol.geom.Geometry|GeoJSONGeometry} geom
 * @return {boolean}
 * @throws {Error}
 */
function isMulti(geom) {
  var multiTypes = [GEOMETRY_TYPE.MULTI_POINT, GEOMETRY_TYPE.MULTI_LINE_STRING, GEOMETRY_TYPE.MULTI_POLYGON, GEOMETRY_TYPE.GEOMETRY_COLLECTION];

  return multiTypes.includes(geom.type || geom.getType());
}

/**
 * @param {ol.geom.Geometry|GeoJSONGeometry} geom
 * @return {ol.geom.SimpleGeometry|GeoJSONGeometry}
 * @throws {Error}
 */
function toSimpleGeom(geom) {
  var type = geom.type || geom.getType();
  var complexTypes = [GEOMETRY_TYPE.GEOMETRY_COLLECTION];

  if (!complexTypes.includes(type)) return geom;

  return (geom.geometries || geom.getGeometries())[0];
}

/**
 * @param {ol.geom.Geometry|GeoJSONGeometry} geom
 * @return {ol.Coordinate|undefined}
 */
function pointOnSurface(geom) {
  var simpleGeom = toSimpleGeom(geom);
  var pointFeature = turfPointOnSurface({
    type: simpleGeom.type || simpleGeom.getType(),
    coordinates: simpleGeom.coordinates || simpleGeom.getCoordinates()
  });

  if (pointFeature && pointFeature.geometry) {
    return pointFeature.geometry.coordinates;
  }
}

var geomHelper = Object.freeze({
	point: point,
	line: line,
	polygon: polygon,
	multiPoint: multiPoint,
	multiLine: multiLine,
	multiPolygon: multiPolygon,
	collection: collection,
	circularPolygon: circularPolygon,
	isMulti: isMulti,
	toSimpleGeom: toSimpleGeom,
	pointOnSurface: pointOnSurface
});

var _transforms;

/**
 * Projection transform helpers
 */
var fromLonLat = olproj.fromLonLat;
var toLonLat = olproj.toLonLat;
var transform = olproj.transform;
var transformExtent = olproj.transformExtent;
var getPointResolution = olproj.getPointResolution;
var get = olproj.get;
var add = olproj.addProjection;
var equivalent = olproj.equivalent;
var addEquivalent = olproj.addEquivalentProjections;
var addCoordinateTransforms = olproj.addCoordinateTransforms;
var getTransform = olproj.getTransform;

function create(options) {
  return new Projection(options);
}

/**
 * @param {number[]} coordinate
 * @param {ol.ProjectionLike} [projection]
 * @return {number[]}
 */
function pointToLonLat(coordinate) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return toLonLat(coordinate, projection);
}
/**
 * @param {number[]} coordinate
 * @param {ol.ProjectionLike} [projection]
 * @return {number[]}
 */
function pointFromLonLat(coordinate) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return fromLonLat(coordinate, projection);
}

function transformPoint(coordinate, sourceProjection, destProjection) {
  return transform(coordinate, sourceProjection, destProjection);
}

/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function lineToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (point) {
    return pointToLonLat(point, projection);
  });
}
/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function lineFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (point) {
    return pointFromLonLat(point, projection);
  });
}

function transformLine(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (point) {
    return transform(point, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function polygonToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (line) {
    return lineToLonLat(line, projection);
  });
}
/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function polygonFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (line) {
    return lineFromLonLat(line, projection);
  });
}

function transformPolygon(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (line) {
    return transformLine(line, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function multiPointToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (point) {
    return pointToLonLat(point, projection);
  });
}
/**
 * @param {Array<number[]>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<number[]>}
 */
function multiPointFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (point) {
    return pointFromLonLat(point, projection);
  });
}

function transformMultiPoint(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (point) {
    return transformPoint(point, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function multiLineToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (line) {
    return lineToLonLat(line, projection);
  });
}
/**
 * @param {Array<Array<number[]>>} coordinates
 * @param {ol.ProjectionLike} [projection]
 * @return {Array<Array<number[]>>}
 */
function multiLineFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (line) {
    return lineFromLonLat(line, projection);
  });
}

function transformMultiLine(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (line) {
    return transformLine(line, sourceProjection, destProjection);
  });
}

/**
 * @param {Array<Array<Array<number[]>>>} coordinates
 * @param {ol.ProjectionLike} projection
 * @return {Array<Array<Array<number[]>>>}
 */
function multiPolygonToLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (polygon) {
    return polygonToLonLat(polygon, projection);
  });
}
/**
 * @param {Array<Array<Array<number[]>>>} coordinates
 * @param {ol.ProjectionLike} projection
 * @return {Array<Array<Array<number[]>>>}
 */
function multiPolygonFromLonLat(coordinates) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return coordinates.map(function (polygon) {
    return polygonFromLonLat(polygon, projection);
  });
}

function transformMultiPolygon(coordinates, sourceProjection, destProjection) {
  return coordinates.map(function (polygon) {
    return transformPolygon(polygon, sourceProjection, destProjection);
  });
}

/**
 * Transforms by geom type
 * @type {Object<string, function>}
 */
var transforms = (_transforms = {}, _defineProperty(_transforms, GEOMETRY_TYPE.POINT, {
  toLonLat: pointToLonLat,
  fromLonLat: pointFromLonLat,
  transform: transformPoint
}), _defineProperty(_transforms, GEOMETRY_TYPE.LINE_STRING, {
  toLonLat: lineToLonLat,
  fromLonLat: lineFromLonLat,
  transform: transformLine
}), _defineProperty(_transforms, GEOMETRY_TYPE.POLYGON, {
  toLonLat: polygonToLonLat,
  fromLonLat: polygonFromLonLat,
  transform: transformPolygon
}), _defineProperty(_transforms, GEOMETRY_TYPE.MULTI_POINT, {
  toLonLat: multiPointToLonLat,
  fromLonLat: multiPointFromLonLat,
  transform: transformMultiPoint
}), _defineProperty(_transforms, GEOMETRY_TYPE.MULTI_LINE_STRING, {
  toLonLat: multiLineToLonLat,
  fromLonLat: multiLineFromLonLat,
  transform: transformMultiLine
}), _defineProperty(_transforms, GEOMETRY_TYPE.MULTI_POLYGON, {
  toLonLat: multiPolygonToLonLat,
  fromLonLat: multiPolygonFromLonLat,
  transform: transformMultiPolygon
}), _transforms);

/**
 * @param {ol.Extent} extent
 * @param {ol.ProjectionLike} [projection=EPSG:3857]
 * @return {ol.Extent}
 */
function extentFromLonLat(extent) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return transformExtent(extent, EPSG_4326, projection);
}

/**
 * @param {ol.Extent} extent
 * @param {ol.ProjectionLike} [projection=EPSG:3857]
 * @return {ol.Extent}
 */
function extentToLonLat(extent) {
  var projection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;

  return transformExtent(extent, projection, EPSG_4326);
}

var proj = Object.freeze({
	fromLonLat: fromLonLat,
	toLonLat: toLonLat,
	transform: transform,
	transformExtent: transformExtent,
	getPointResolution: getPointResolution,
	get: get,
	add: add,
	equivalent: equivalent,
	addEquivalent: addEquivalent,
	addCoordinateTransforms: addCoordinateTransforms,
	getTransform: getTransform,
	create: create,
	pointToLonLat: pointToLonLat,
	pointFromLonLat: pointFromLonLat,
	transformPoint: transformPoint,
	lineToLonLat: lineToLonLat,
	lineFromLonLat: lineFromLonLat,
	transformLine: transformLine,
	polygonToLonLat: polygonToLonLat,
	polygonFromLonLat: polygonFromLonLat,
	transformPolygon: transformPolygon,
	multiPointToLonLat: multiPointToLonLat,
	multiPointFromLonLat: multiPointFromLonLat,
	transformMultiPoint: transformMultiPoint,
	multiLineToLonLat: multiLineToLonLat,
	multiLineFromLonLat: multiLineFromLonLat,
	transformMultiLine: transformMultiLine,
	multiPolygonToLonLat: multiPolygonToLonLat,
	multiPolygonFromLonLat: multiPolygonFromLonLat,
	transformMultiPolygon: transformMultiPolygon,
	transforms: transforms,
	extentFromLonLat: extentFromLonLat,
	extentToLonLat: extentToLonLat
});

/**
 * @param {*} value
 * @return {boolean} True if value is number or numeric string.
 */
function isNumeric(value) {
  return !isNaN(parseFloat(value)) && isFinite(value);
}

/**
 * Style helpers
 */
var reduceWithKey = _reduce.convert({ cap: false });

/**
 * @return {VlStyle[]}
 */
function defaultStyle() {
  return [{
    fillColor: [255, 255, 255, 0.4],
    strokeColor: '#3399CC',
    strokeWidth: 1.25,
    imageRadius: 5
  }];
}

/**
 * @return {Object<GEOMETRY_TYPE, VlStyle[]>}
 */
function defaultEditStyle() {
  /** @type {Object<GEOMETRY_TYPE, VlStyle[]>} */
  var styles = {};
  var white = [255, 255, 255, 1];
  var blue = [0, 153, 255, 1];
  var width = 3;

  styles[GEOMETRY_TYPE.LINE_STRING] = [{
    strokeColor: white,
    strokeWidth: width + 2
  }, {
    strokeColor: blue,
    strokeWidth: width
  }];
  styles[GEOMETRY_TYPE.MULTI_LINE_STRING] = styles[GEOMETRY_TYPE.LINE_STRING];

  styles[GEOMETRY_TYPE.POLYGON] = [{
    fillColor: [255, 255, 255, 0.5]
  }].concat(styles[GEOMETRY_TYPE.LINE_STRING]);
  styles[GEOMETRY_TYPE.MULTI_POLYGON] = styles[GEOMETRY_TYPE.POLYGON];

  styles[GEOMETRY_TYPE.CIRCLE] = styles[GEOMETRY_TYPE.POLYGON].concat(styles[GEOMETRY_TYPE.LINE_STRING]);

  styles[GEOMETRY_TYPE.POINT] = [{
    imageRadius: width * 2,
    fillColor: blue,
    strokeColor: white,
    strokeWidth: width / 2,
    zIndex: Infinity
  }];
  styles[GEOMETRY_TYPE.MULTI_POINT] = styles[GEOMETRY_TYPE.POINT];

  styles[GEOMETRY_TYPE.GEOMETRY_COLLECTION] = styles[GEOMETRY_TYPE.POLYGON].concat(styles[GEOMETRY_TYPE.LINE_STRING], styles[GEOMETRY_TYPE.POINT]);

  return styles;
}

var isEmpty = function isEmpty(x) {
  if (x == null) return true;
  if (typeof x === 'number') return false;

  return (typeof x === 'string' || Array.isArray(x)) && !x.length || !_Object$keys(x).length;
};

/**
 * @param {VlStyle} vlStyle
 * @return {ol.style.Style|undefined}
 */
function style(vlStyle) {
  if (isEmpty(vlStyle)) return;

  var olStyle = {
    text: text(vlStyle),
    fill: fill(vlStyle),
    stroke: stroke(vlStyle),
    image: image(vlStyle),
    geometry: geom(vlStyle),
    zIndex: vlStyle.zIndex
  };

  if (!isEmpty(olStyle)) {
    return new Style(olStyle);
  }
}

var addPrefix = function addPrefix(prefix) {
  return function (str) {
    return prefix + (prefix ? _upperFirst(str) : str);
  };
};

/**
 * @param {*} color
 * @returns {*}
 */
function normalizeColor(color) {
  var c = color;

  if (typeof color === 'string') {
    c = parseColor(color).rgba;
  }

  return c;
}

/**
 * @param {VlStyle} vlStyle
 * @param {string} [prefix]
 * @returns {ol.style.Fill|undefined}
 */
function fill(vlStyle) {
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

  var prefixKey = addPrefix(prefix);
  var keys = ['fillColor'].map(prefixKey);
  var compiledKey = prefixKey('fill');

  // check on already compiled style existence
  if (vlStyle[compiledKey] instanceof Fill) return vlStyle[compiledKey];

  var transform = _flow(_pick(keys), reduceWithKey(function (result, value, name) {
    name = _lowerFirst(name.replace(new RegExp(prefixKey('fill')), ''));

    if (name === 'color') {
      value = normalizeColor(value);
    }

    result[name] = value;

    return result;
  }, {}));

  var fillStyle = transform(vlStyle);

  if (!isEmpty(fillStyle)) {
    return new Fill(fillStyle);
  }
}

/**
 * @param {VlStyle} vlStyle
 * @param {string} [prefix]
 * @returns {ol.style.Stroke|undefined}
 */
function stroke(vlStyle) {
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

  var prefixKey = addPrefix(prefix);
  var keys = ['strokeColor', 'strokeWidth', 'strokeDash', 'strokeCap', 'strokeJoin'].map(prefixKey);
  var compiledKey = prefixKey('stroke');

  if (vlStyle[compiledKey] instanceof Stroke) return vlStyle[compiledKey];

  var transform = _flow(_pick(keys), reduceWithKey(function (result, value, name) {
    switch (name) {
      case prefixKey('strokeColor'):
      case prefixKey('strokeWidth'):
        name = _lowerFirst(name.replace(new RegExp(prefixKey('stroke')), ''));
        break;
      case prefixKey('strokeDash'):
      case prefixKey('strokeCap'):
      case prefixKey('strokeJoin'):
        name = 'line' + name.replace(new RegExp(prefixKey('stroke')), '');
        break;
    }

    if (name === 'color') {
      value = normalizeColor(value);
    }

    result[name] = value;

    return result;
  }, {}));

  var strokeStyle = transform(vlStyle);

  if (!isEmpty(strokeStyle)) {
    return new Stroke(strokeStyle);
  }
}

/**
 * @param {VlStyle} vlStyle
 * @returns {ol.style.Image|undefined}
 * @todo split to separate circle, regShape, Icon
 */
function image(vlStyle) {
  if (isEmpty(vlStyle.imageSrc) && isEmpty(vlStyle.image) && isEmpty(vlStyle.imagePoints) && !isNumeric(vlStyle.imageRadius)) {
    return;
  }

  if (vlStyle.image instanceof ImageStyle) return vlStyle.image;

  var imageStyle = void 0,
      Ctor = void 0;

  if (!isEmpty(vlStyle.imageSrc) || !isEmpty(vlStyle.image)) {
    // icon construction
    Ctor = Icon;
    // then create Icon options
    imageStyle = _extends({}, vlStyle, {
      anchor: vlStyle.imageAnchor,
      anchorOrigin: vlStyle.imageAnchorOrigin,
      color: vlStyle.imageColor,
      offset: vlStyle.imageOffset,
      offsetOrigin: vlStyle.imageOffsetOrigin,
      opacity: vlStyle.imageOpacity,
      scale: vlStyle.imageScale,
      rotation: vlStyle.imageRotation,
      size: vlStyle.imageSize,
      img: vlStyle.image,
      imgSize: vlStyle.imageImgSize,
      src: vlStyle.imageSrc,
      crossOrigin: vlStyle.imageCrossOrigin
    });
  } else if (vlStyle.imagePoints != null) {
    // regular shape construction
    Ctor = RegularShape;
    // create RegularShape options
    imageStyle = _extends({}, vlStyle, {
      points: vlStyle.imagePoints,
      radius: vlStyle.imageRadius,
      radius1: vlStyle.imageRadius1,
      radius2: vlStyle.imageRadius2,
      angle: vlStyle.imageAngle,
      rotation: vlStyle.imageRotation
    });
  } else {
    // circle construction
    Ctor = Circle;
    // create Circle options
    imageStyle = _extends({}, vlStyle, {
      radius: vlStyle.imageRadius
    });
  }

  imageStyle = _extends({}, imageStyle, {
    fill: fill(vlStyle, 'image') || fill(vlStyle),
    stroke: stroke(vlStyle, 'image') || stroke(vlStyle),
    snapToPixel: true
  });

  if (!isEmpty(imageStyle)) {
    return new Ctor(imageStyle);
  }
}

/**
 * @param {VlStyle} vlStyle
 * @returns {ol.style.Text|undefined}
 */
function text(vlStyle) {
  // noinspection JSValidateTypes
  if (vlStyle.text == null) return;
  if (vlStyle.text instanceof Text) return vlStyle.text;

  var textStyle = {
    text: vlStyle.text
  };

  var fontSize = vlStyle.textFontSize ? vlStyle.textFontSize + 'px' : undefined;
  var font = ['normal', fontSize, vlStyle.textFont].filter(function (x) {
    return !!x;
  }).join(' ');

  _Object$assign(textStyle, _pick(['textScale', 'textRotation', 'textOffsetX', 'textOffsetY', 'textAlign'], vlStyle), {
    font: font,
    fill: fill(vlStyle, 'text') || fill(vlStyle),
    stroke: stroke(vlStyle, 'text') || stroke(vlStyle)
  });

  if (!isEmpty(textStyle)) {
    return new Text(textStyle);
  }
}

/**
 * @param {VlStyle} vlStyle
 * @return {ol.geom.Geometry|ol.StyleGeometryFunction|undefined}
 */
function geom(vlStyle) {
  if (_isFunction(vlStyle.geom)) {
    return function __styleGeomFunc(feature) {
      return vlStyle.geom(feature, geomHelper);
    };
  }

  return vlStyle.geom;
}

/**
 * @typedef {ol.style.Style|ol.style.Image|ol.style.Fill|ol.style.Stroke|ol.style.Text|ol.StyleFunction} OlStyle
 */

/**
 * @typedef {Object} VlStyle
 *
 * Shared
 * @property {string|number[]|undefined} fillColor
 * @property {string|number[]|undefined} strokeColor
 * @property {number|undefined} strokeWidth
 * @property {number[]|undefined} strokeDash
 * @property {string|undefined} strokeCap
 * @property {string|undefined} strokeJoin
 * @property {number|undefined} zIndex
 * @property {ol.style.Fill|undefined} fill
 * @property {ol.style.Stroke|undefined} stroke
 *
 * Text only
 * @property {string|ol.style.Text|undefined} text
 * @property {string|undefined} textFont
 * @property {number|undefined} textFontSize
 * @property {string|number[]|undefined} textFillColor
 * @property {string|number[]|undefined} textStrokeColor
 * @property {number|undefined} textStrokeWidth
 * @property {number[]|undefined} textStrokeDash
 * @property {string|undefined} textStrokeCap
 * @property {string|undefined} textStrokeJoin
 * @property {number|undefined} textScale
 * @property {string|undefined} textAlign
 * @property {number|undefined} textRotation
 * @property {number|undefined} textOffsetX
 * @property {number|undefined} textOffsetY
 * @property {ol.style.Stroke|undefined} textStroke
 * @property {ol.style.Fill|undefined} textFill
 *
 * Image only
 * @property {ol.style.Image|undefined} image
 * @property {string|undefined} imageSrc
 * @property {number[]|undefined} imageSize
 * @property {number[]|undefined} imageImgSize
 * @property {number|undefined} imageOffset
 * @property {number[]|undefined} imageAnchor
 * @property {number|undefined} imageScale
 * @property {number|undefined} imageRotation
 * @property {number|undefined} imageRadius
 * @property {number|undefined} imageRadius1
 * @property {number|undefined} imageRadius2
 * @property {number|undefined} imagePoints
 * @property {number|undefined} imageAngle
 * @property {number|undefined} imageOpacity
 * @property {string|number[]|undefined} imageFillColor
 * @property {string|number[]|undefined} imageStrokeColor
 * @property {number|undefined} imageStrokeWidth
 * @property {number[]|undefined} imageStrokeDash
 * @property {string|undefined} imageStrokeCap
 * @property {string|undefined} imageStrokeJoin
 * @property {IconOrigin|undefined} imageAnchorOrigin
 * @property {ColorLike|undefined} imageColor
 * @property {IconOrigin|undefined} imageOffsetOrigin
 * @property {ol.style.Stroke|undefined} imageStroke
 * @property {ol.style.Fill|undefined} imageFill
 * @property {string|undefined} imageCrossOrigin
 *
 * @property {ol.geom.Geometry|ol.StyleGeometryFunction|undefined} geom Coordinates should be in map projection
 */

var style$1 = Object.freeze({
	defaultStyle: defaultStyle,
	defaultEditStyle: defaultEditStyle,
	style: style,
	normalizeColor: normalizeColor,
	fill: fill,
	stroke: stroke,
	image: image,
	text: text,
	geom: geom
});

var createXYZ = oltilegrid.createXYZ;


/**
 * Create a resolutions array from an extent.  A zoom factor of 2 is assumed.
 * @param {ol.Extent} extent Extent.
 * @param {number=} maxZoom Maximum zoom level (default is
 *     ol.DEFAULT_MAX_ZOOM).
 * @param {number|ol.Size=} tileSize Tile size (default uses
 *     ol.DEFAULT_TILE_SIZE).
 * @return {!Array.<number>} Resolutions array.
 * @see https://github.com/openlayers/openlayers/blob/master/src/ol/tilegrid.js#L104
 */
function resolutionsFromExtent(extent) {
  var maxZoom = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MAX_ZOOM;
  var tileSize = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : TILE_SIZE;

  tileSize = olsize.toSize(tileSize);

  var height = getHeight(extent);
  var width = getWidth(extent);

  var maxResolution = Math.max(width / tileSize[0], height / tileSize[1]);

  var length = maxZoom + 1;
  var resolutions = new Array(length);

  for (var z = 0; z < length; ++z) {
    resolutions[z] = maxResolution / Math.pow(2, z);
  }

  return resolutions;
}

/**
 * @param {ol.Extent} extent Extent.
 * @param {number=} maxZoom Maximum zoom level (default is MAX_ZOOM).
 * @param {number|ol.Size=} tileSize Tile size (default uses TILE_SIZE).
 * @param {string|ol.extent.Corner} [corner] Extent corner (default is EXTENT_CORNER.TOP_LEFT).
 * @return {!ol.tilegrid.TileGrid} TileGrid instance.
 * @see https://github.com/openlayers/openlayers/blob/master/src/ol/tilegrid.js#L58
 */
function createForExtent(extent) {
  var maxZoom = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MAX_ZOOM;
  var tileSize = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : TILE_SIZE;
  var corner = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : EXTENT_CORNER.TOP_LEFT;

  var resolutions = resolutionsFromExtent(extent, maxZoom, tileSize);

  return new TileGrid({
    extent: extent,
    origin: getCorner(extent, corner),
    resolutions: resolutions,
    tileSize: tileSize
  });
}

/**
 * @param {ol.ProjectionLike} projection Projection.
 * @param {number=} maxZoom Maximum zoom level (default is
 *     ol.DEFAULT_MAX_ZOOM).
 * @param {number|ol.Size=} tileSize Tile size (default uses ol.DEFAULT_TILE_SIZE).
 * @param {string|ol.extent.Corner=} corner Extent corner (default is
 *     ol.extent.Corner.BOTTOM_LEFT).
 * @return {!ol.tilegrid.TileGrid} TileGrid instance.
 * @see https://github.com/openlayers/openlayers/blob/master/src/ol/tilegrid.js#L135
 */
function createForProjection(projection) {
  var maxZoom = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MAX_ZOOM;
  var tileSize = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : TILE_SIZE;
  var corner = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : EXTENT_CORNER.BOTTOM_LEFT;

  return createForExtent(fromProjection(projection), maxZoom, tileSize, corner);
}

var tileGrid = Object.freeze({
	createXYZ: createXYZ,
	resolutionsFromExtent: resolutionsFromExtent,
	createForExtent: createForExtent,
	createForProjection: createForProjection
});

var all = loadingstrategy.all;
var bbox = loadingstrategy.bbox;
var tile = loadingstrategy.tile;


var loadStrategy = Object.freeze({
	all: all,
	bbox: bbox,
	tile: tile
});

/**
 * @param {GeoJSONFeature|Vue|ol.Feature|string|number} feature
 * @return {string|number}
 * @throws {Error}
 */
function getId(feature) {
  var id = void 0;
  if (_isPlainObject(feature) || feature instanceof Vue) {
    id = feature.id;
  } else if (feature instanceof Feature) {
    id = feature.getId();
  } else if (_isString(feature) || _isNumber(feature)) {
    id = feature;
  } else {
    throw new Error('Illegal feature format');
  }

  return id;
}

var feature = Object.freeze({
	getId: getId
});

var AssertionError = function (_Error) {
  _inherits(AssertionError, _Error);

  function AssertionError(message) {
    _classCallCheck(this, AssertionError);

    var _this = _possibleConstructorReturn(this, (AssertionError.__proto__ || _Object$getPrototypeOf(AssertionError)).call(this, message));

    _this.name = _this.constructor.name;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(_this, _this.constructor);
    } else {
      _this.stack = new Error(message).stack;
    }
    return _this;
  }

  return AssertionError;
}(Error);

function assert(value, message) {
  if (!value) {
    throw new AssertionError(message || 'Assertion failed');
  }
}

function ok(value, message) {
  assert(ok, message);
}

/**
 * @param {*} value
 * @throws {AssertionError}
 */
function numeric(value) {
  assert(isNumeric(value), 'value is a number');
}

/**
 * @param {*} value
 * @param {Function} Ctor
 * @throws {AssertionError}
 */
function instanceOf(value, Ctor) {
  assert(value instanceof Ctor, 'value is an instance of ' + Ctor.name);
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasMap(vm) {
  assert(vm.$map, 'component has "$map" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasView(vm) {
  assert(vm.$view, 'component has "$view" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasGeolocation(vm) {
  assert(vm.$geolocation, 'component has "$geolocation" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasFeature(vm) {
  assert(vm.$feature, 'component has "$feature" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasLayer(vm) {
  assert(vm.$layer, 'component has "$layer" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasSource(vm) {
  assert(vm.$source, 'component has "$source" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasGeometry(vm) {
  assert(vm.$geometry, 'component has "$geometry" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasInteraction(vm) {
  assert(vm.$interaction, 'component has "$interaction" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasStyle(vm) {
  assert(vm.$style, 'component has "$style" property');
}

/**
 * @param {Object} vm
 * @return {void}
 * @throws {AssertionError}
 */
function hasOverlay(vm) {
  assert(vm.$overlay, 'component has "$overlay" property');
}

var assert$1 = Object.freeze({
	AssertionError: AssertionError,
	assert: assert,
	ok: ok,
	numeric: numeric,
	instanceOf: instanceOf,
	hasMap: hasMap,
	hasView: hasView,
	hasGeolocation: hasGeolocation,
	hasFeature: hasFeature,
	hasLayer: hasLayer,
	hasSource: hasSource,
	hasGeometry: hasGeometry,
	hasInteraction: hasInteraction,
	hasStyle: hasStyle,
	hasOverlay: hasOverlay
});

/**
 * @alias module:core/options
 */
var options = {
  methods: {
    /**
     * Returns option from global VueLayers config by key.
     * @param {string} key
     * @param {*} [def]
     * @return {*}
     */
    globOption: function globOption(key, def) {
      return _getOr(def, key, this[VL_OPTIONS]);
    }
  }
};

/**
 * Mixin with helpers for projection transforms between current view projection and global defined projection.
 * @module core/proj-transforms
 */
/**
 * @alias module:core/proj-transforms
 * @vueProto
 */
var projTransforms = {
  mixins: [options],
  computed: {
    viewProjection: function viewProjection() {
      return this.$view ? this.$view.getProjection().getCode() : EPSG_3857;
    },
    bindProjection: function bindProjection() {
      return this.globOption('bindToProj', this.viewProjection);
    }
  },
  methods: {
    pointToViewProj: function pointToViewProj(point) {
      return transformPoint(point, this.bindProjection, this.viewProjection);
    },
    pointToBindProj: function pointToBindProj(point) {
      return transformPoint(point, this.viewProjection, this.bindProjection);
    },
    lineToViewProj: function lineToViewProj(line) {
      return transformLine(line, this.bindProjection, this.viewProjection);
    },
    lineToBindProj: function lineToBindProj(line) {
      return transformLine(line, this.viewProjection, this.bindProjection);
    },
    polygonToViewProj: function polygonToViewProj(polygon) {
      return transformPolygon(polygon, this.bindProjection, this.viewProjection);
    },
    polygonToBindProj: function polygonToBindProj(polygon) {
      return transformPolygon(polygon, this.viewProjection, this.bindProjection);
    },
    multiPointToViewProj: function multiPointToViewProj(multiPoint) {
      return transformMultiPoint(multiPoint, this.bindProjection, this.viewProjection);
    },
    multiPointToBindProj: function multiPointToBindProj(multiPoint) {
      return transformMultiPoint(multiPoint, this.viewProjection, this.bindProjection);
    },
    multiLineToViewProj: function multiLineToViewProj(multiLine) {
      return transformMultiLine(multiLine, this.bindProjection, this.viewProjection);
    },
    multiLineToBindProj: function multiLineToBindProj(multiLine) {
      return transformMultiLine(multiLine, this.viewProjection, this.bindProjection);
    },
    multiPolygonToViewProj: function multiPolygonToViewProj(multiPolygon) {
      return transformMultiPolygon(multiPolygon, this.bindProjection, this.viewProjection);
    },
    multiPolygonToBindProj: function multiPolygonToBindProj(multiPolygon) {
      return transformMultiPolygon(multiPolygon, this.viewProjection, this.bindProjection);
    },
    extentToViewProj: function extentToViewProj(extent) {
      return transformExtent(extent, this.bindProjection, this.viewProjection);
    },
    extentToBindProj: function extentToBindProj(extent) {
      return transformExtent(extent, this.viewProjection, this.bindProjection);
    },
    writeGeometryInBindProj: function writeGeometryInBindProj(geometry) {
      return writeGeometry(geometry, this.viewProjection, this.bindProjection);
    },
    readGeometryInBindProj: function readGeometryInBindProj(geometry) {
      return readGeometry(geometry, this.viewProjection, this.bindProjection);
    },
    writeFeatureInBindProj: function writeFeatureInBindProj(feature) {
      return writeFeature(feature, this.viewProjection, this.bindProjection);
    },
    readFeatureInBindProj: function readFeatureInBindProj(feature) {
      return readFeature(feature, this.viewProjection, this.bindProjection);
    }
  }
};

var methods = {
  /**
   * @return {{
   *     addFeature: function(ol.Feature): void,
   *     removeFeature: function(ol.Feature): void,
   *     hasFeature: function(ol.Feature): bool
   *   }|undefined}
   * @protected
   */
  getFeaturesTarget: function getFeaturesTarget() {
    throw new Error('Not implemented method');
  },

  /**
   * @param {Array<(ol.Feature|Vue|GeoJSONFeature)>} features
   * @return {void}
   */
  addFeatures: function addFeatures(features) {
    var _this = this;

    features.forEach(function (feature) {
      return _this.addFeature(feature);
    });
  },

  /**
   * @param {ol.Feature|Vue|GeoJSONFeature} feature
   * @return {void}
   */
  addFeature: function addFeature(feature) {
    if (feature instanceof Vue) {
      feature = feature.$feature;
    } else if (_isPlainObject(feature)) {
      feature = this.readFeatureInBindProj(feature);
    }
    instanceOf(feature, Feature);

    this.prepareFeature(feature);

    if (!this._features[feature.getId()]) {
      this._features[feature.getId()] = feature;
    }

    var featuresTarget = this.getFeaturesTarget();
    if (featuresTarget && !featuresTarget.hasFeature(feature)) {
      featuresTarget.addFeature(feature);
    }
  },

  /**
   * @param {Array<(ol.Feature|Vue|GeoJSONFeature)>} features
   * @return {void}
   */
  removeFeatures: function removeFeatures(features) {
    features.forEach(this.removeFeature);
  },

  /**
   * @param {ol.Feature|Vue|GeoJSONFeature} feature
   * @return {void}
   */
  removeFeature: function removeFeature(feature) {
    if (feature instanceof Vue) {
      feature = feature.$feature;
    } else if (_isPlainObject(feature)) {
      feature = this._features[feature.id];
    }
    if (!feature) return;

    delete this._features[feature.getId()];

    var featuresTarget = this.getFeaturesTarget();
    if (featuresTarget && featuresTarget.hasFeature(feature)) {
      featuresTarget.removeFeature(feature);
    }
  },

  /**
   * @return {void}
   */
  clear: function clear() {
    this._features = _Object$create(null);
  },

  /**
   * @param {string|number} id
   * @return {ol.Feature|undefined}
   */
  getFeatureById: function getFeatureById(id) {
    return this._features[id];
  },

  /**
   * @return {ol.Feature[]}
   */
  getFeatures: function getFeatures() {
    return _Object$values(this._features);
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return {
      get featuresContainer() {
        return vm;
      }
    };
  },

  /**
   * @param {ol.Feature} feature
   * @return {ol.Feature}
   * @protected
   */
  prepareFeature: function prepareFeature(feature) {
    if (feature.getId() == null) {
      feature.setId(uuid());
    }

    return feature;
  }
};

var featuresContainer = {
  mixins: [projTransforms],
  methods: methods,
  created: function created() {
    /**
     * @type {Object<string, ol.Feature>}
     * @private
     */
    this._features = _Object$create(null);
  },
  destroyed: function destroyed() {
    this._features = _Object$create(null);
  }
};

/**
 * @param {...Object} args
 * @returns {Object}
 */
function multiMergeDescriptors() {
  var redefine = void 0;

  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  if (_typeof(args[args.length - 1]) !== 'object') {
    redefine = args.pop();
  }

  return args.slice(1).reduce(function (dest, src, i) {
    return mergeDescriptors(dest, src, redefine);
  }, args[0]);
}

/**
 * Simple Identity map with refs count
 */
var IdentityMap = function () {
  function IdentityMap() {
    _classCallCheck(this, IdentityMap);

    this.map = _Object$create(null);
  }

  _createClass(IdentityMap, [{
    key: "set",
    value: function set(id, value) {
      if (value == null) return;

      this.map[id] = {
        value: value,
        refs: 1
      };
    }
  }, {
    key: "get",
    value: function get(id) {
      var rec = this.map[id];
      if (!rec || rec.value == null) return;

      rec.refs++;
      this.map[id] = rec;

      return rec.value;
    }
  }, {
    key: "unset",
    value: function unset(id) {
      var rec = this.map[id];
      if (!rec || rec.value == null) return;

      rec.refs--;
      if (rec.refs === 0) {
        delete this.map[id];
      } else {
        this.map[id] = rec;
      }
    }
  }, {
    key: "has",
    value: function has(id) {
      return !!this.map[id];
    }
  }, {
    key: "ids",
    value: function ids() {
      return _Object$keys(this.map);
    }
  }, {
    key: "refs",
    value: function refs(id) {
      return this.has(id) ? this.map[id].refs : undefined;
    }
  }]);

  return IdentityMap;
}();

var identMap = {
  props: {
    /**
     * Unique key for saving to identity map
     * @type {string|number}
     * @experimental
     */
    ident: [String, Number]
  },
  methods: {
    getFullIdent: function getFullIdent() {
      if (!this.ident) return;

      for (var _len = arguments.length, parts = Array(_len), _key = 0; _key < _len; _key++) {
        parts[_key] = arguments[_key];
      }

      return [this.$options.name, this.ident].concat(_toConsumableArray(parts)).filter(_identity).join(':');
    }
  },
  created: function created() {
    initIdentityMap.call(this);
  }
};

function initIdentityMap() {
  var _this = this;

  if (!this[IDENTITY_MAP_PROP]) {
    Vue[IDENTITY_MAP_PROP] = Vue.prototype[IDENTITY_MAP_PROP] = new IdentityMap();
  }
  _Object$defineProperties(this, {
    $identityMap: {
      enumerable: true,
      get: function get() {
        return _this[IDENTITY_MAP_PROP];
      }
    }
  });
}

function log() {
  var _console;

  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  (_console = console).log.apply(_console, ['[VueLayers]'].concat(args));
}

function warn(msg) {
  var _console2;

  for (var _len2 = arguments.length, args = Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    args[_key2 - 1] = arguments[_key2];
  }

  (_console2 = console).warn.apply(_console2, ['[VueLayers] WARNING: ' + msg].concat(args));
}

function error(msg) {
  var _console3;

  for (var _len3 = arguments.length, args = Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
    args[_key3 - 1] = arguments[_key3];
  }

  (_console3 = console).error.apply(_console3, ['[VueLayers] ERROR: ' + msg].concat(args));
}

function logdbg() {
  {
    log.apply(undefined, arguments);
  }
}

function warndbg() {
  {
    warn.apply(undefined, arguments);
  }
}

function errordbg() {
  {
    error.apply(undefined, arguments);
  }
}

var log$1 = Object.freeze({
	log: log,
	warn: warn,
	error: error,
	logdbg: logdbg,
	warndbg: warndbg,
	errordbg: errordbg
});

var noop = function noop() {};

/**
 * RxJS subscriptions manager.
 */
var rxSubs = {
  methods: {
    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {},

    /**
     * @return {void}
     * @protected
     */
    unsubscribeAll: function unsubscribeAll() {
      this._rxSubs.forEach(function (x) {
        return x.unsubscribe();
      });
      this._rxSubs = [];
    },

    /**
     * @param {Observable<T>} observable
     * @param {function} [next] Next handler or Observer object.
     * @param {function} [error] Error handler.
     * @param {function} [complete] Complete handler.
     * @return {Subscription}
     * @protected
     */
    subscribeTo: function subscribeTo(observable) {
      var next = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;

      var _error = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;

      var complete = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;

      _error = function error$$1(err) {
        errordbg(err.stack);
        _error(err);
      };

      var subs = observable.subscribe(next, _error, complete);
      this._rxSubs.push(subs);

      return subs;
    }
  },
  beforeCreate: function beforeCreate() {
    /**
     * @type {Subscription[]}
     * @private
     */
    this._rxSubs = [];
  },
  destroyed: function destroyed() {
    this.unsubscribeAll();
  }
};

/**
 * Service container mixin
 */
var services = {
  inject: {
    $services: SERVICES_PROP
  },
  provide: function provide() {
    return _defineProperty({}, SERVICES_PROP, this.getServices());
  },

  methods: {
    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      return multiMergeDescriptors({}, this.$services || {});
    }
  },
  beforeCreate: function beforeCreate() {
    // bloody patch to suppress Vue warning about not provided
    // $services key for the root ol-cmp instance
    var source = this;

    while (source) {
      if (source._provided && SERVICES_PROP in source._provided) {
        break;
      }
      source = source.$parent;
    }

    if (!source) {
      if (!this._provided) {
        this._provided = {};
      }

      this._provided[SERVICES_PROP] = undefined;
    }
  }
};

var props$1 = {};

var INSTANCE_PROMISE = 'instancePromise';
var methods$2 = {
  /**
   * @return {Promise<void>}
   * @protected
   */
  beforeInit: function beforeInit() {},

  /**
   * @return {Promise} Resolves when initialization completes
   * @protected
   */
  init: function init() {
    var _this = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var createPromise, ident;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              createPromise = void 0;
              ident = _this.getFullIdent(INSTANCE_PROMISE);

              if (ident && _this.$identityMap.has(ident)) {
                createPromise = _this.$identityMap.get(ident);
              } else {
                createPromise = _Promise.resolve(_this.createOlObject());

                if (ident) {
                  _this.$identityMap.set(ident, createPromise);
                }
              }

              _context.next = 5;
              return createPromise;

            case 5:
              _this._olObject = _context.sent;

              _this._olObject[VM_PROP] || (_this._olObject[VM_PROP] = []);

              if (!_this._olObject[VM_PROP].includes(_this)) {
                // for loaded from IdentityMap
                _this._olObject[VM_PROP].push(_this);
              }

              _this.rev++;

            case 9:
            case 'end':
              return _context.stop();
          }
        }
      }, _callee, _this);
    }))();
  },

  /**
   * @return {*|Promise<T>}
   * @protected
   * @abstract
   */
  createOlObject: function createOlObject() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    var _this2 = this;

    var ident = this.getFullIdent(INSTANCE_PROMISE);
    if (ident) {
      this.$identityMap.unset(ident);
    }
    if (this._olObject) {
      this._olObject[VM_PROP] = this._olObject[VM_PROP].filter(function (vm) {
        return vm !== _this2;
      });
      this._olObject = undefined;
    }
  },

  /**
   * Redefine for easy call in child components
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    return services.methods.getServices.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   * @abstract
   */
  mount: function mount() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   * @abstract
   */
  unmount: function unmount() {
    throw new Error('Not implemented method');
  },

  /**
   * Refresh internal ol objects
   * @return {Promise}
   */
  refresh: refresh

  /**
   * Basic ol component mixin.
   *
   * @title olCmp
   * @vueProto
   * @alias module:core/mixins/ol-cmp
   *
   * @fires module:core/mixins/ol-cmp#created
   * @fires module:core/mixins/ol-cmp#mounted
   * @fires module:core/mixins/ol-cmp#destroyed
   */
};var cmp$1 = {
  mixins: [options, identMap, rxSubs, services],
  props: props$1,
  methods: methods$2,
  /**
   * @this module:core/mixins/ol-cmp
   */
  data: function data() {
    return (/** @lends module:core/mixins/ol-cmp# */{
        rev: 0
      }
    );
  },
  created: function created() {
    var _this3 = this,
        _context2;

    /**
     * @type {*}
     * @private
     */
    this._olObject = undefined;
    /**
     * @type {Promise<Vue<T>>}
     * @private
     */
    this._createPromise = _Promise.resolve(this.beforeInit()).then(this.init).then(function () {
      // logdbg('created', this.$options.name)
      _this3.$emit('created');
      return _this3;
    });
    /**
     * @type {Promise<Vue<T>>}
     * @private
     */
    this._mountPromise = (_context2 = (_context2 = (_context2 = (_context2 = interval$1.call(Observable$1, 100), skipWhile$1).call(_context2, function () {
      return !_this3._mounted;
    }), first$1).call(_context2), map$1).call(_context2, function () {
      return _this3;
    }), toPromise$1).call(_context2, _Promise);

    _Object$defineProperties(this, {
      $olObject: {
        enumerable: true,
        get: function get() {
          return _this3._olObject;
        }
      },
      $createPromise: {
        enumerable: true,
        get: function get() {
          return _this3._createPromise;
        }
      },
      $mountPromise: {
        enumerable: true,
        get: function get() {
          return _this3._mountPromise;
        }
      }
    });
  },
  mounted: function mounted() {
    var _this4 = this;

    this.$createPromise.then(this.mount).then(function () {
      _this4._mounted = true;
      _this4.$emit('mounted');
      // logdbg('mounted', this.$options.name)
    });
  },
  destroyed: function destroyed() {
    var _this5 = this;

    this.$mountPromise.then(this.unmount).then(this.deinit).then(function () {
      _this5.$emit('destroyed');
      _this5._olObject = _this5._createPromise = _this5._mountPromise = undefined;
      // logdbg('destroyed', this.$options.name)
    });
  }
};

/**
 * @return {Promise<void>}
 */
function refresh() {
  var _this6 = this;

  return new _Promise(function (resolve) {
    if (_this6.$olObject && _isFunction(_this6.$olObject.changed)) {
      _this6.$olObject.once('change', function () {
        return resolve();
      });
      _this6.$olObject.changed();
    } else {
      resolve();
    }
  });
}

/**
 * Emitted when underlying **OpenLayers** instance created.
 * @event module:core/mixins/ol-cmp#created
 * @type {void}
 */
/**
 * Emitted when underlying **OpenLayers** instance mounted to parent.
 * @event module:core/mixins/ol-cmp#mounted
 * @type {void}
 */
/**
 * Emitted when underlying **OpenLayers** instance destroyed.
 * @event module:core/mixins/ol-cmp#destroyed
 * @type {void}
 */

/**
 * Renders stub VNode for component.
 */
var stubVNode = {
  /**
   * @param {function} h
   * @returns {VNode}
   */
  render: function render(h) {
    var options = this.$options.stubVNode || {};
    // render as HTML comment
    if (options.empty) {
      var vnode = h();
      if (typeof options.empty === 'string') {
        vnode.text = options.empty;
      } else if (typeof options.empty === 'function') {
        vnode.text = options.empty.call(this);
      }

      return vnode;
    }

    var children = void 0;
    if (options.slots === false) {
      children = undefined;
    } else {
      children = extractChildren(this.$slots, options.slots);
    }

    var attrs = typeof options.attrs === 'function' ? options.attrs.call(this) : options.attrs;

    var data = {
      attrs: attrs,
      style: {
        display: 'none !important'
      }
    };

    return h(options.tag || 'i', data, children);
  }
};

function extractChildren(slots) {
  var slotNames = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

  return _Object$keys(slots).reduce(function (all, name) {
    if (!slotNames.length || slotNames.includes(name)) {
      all = all.concat(slots[name]);
    }

    return all;
  }, []);
}

/**
 * Basic ol component with stub VNode, i.e. virtual component
 */
var cmp = {
  mixins: [stubVNode, cmp$1],
  methods: {
    /**
     * @return {Promise}
     * @protected
     */
    init: function init() {
      return cmp$1.methods.init.call(this);
    },

    /**
     * @return {void|Promise<void>}
     * @protected
     */
    deinit: function deinit() {
      return cmp$1.methods.deinit.call(this);
    },

    /**
     * @return {*}
     * @protected
     * @abstract
     */
    createOlObject: function createOlObject() {
      throw new Error('Not implemented method');
    },

    /**
     * Redefine for easy call in child components
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      return cmp$1.methods.getServices.call(this);
    },

    /**
     * Refresh internal ol objects
     * @return {Promise}
     */
    refresh: function refresh() {
      return cmp$1.methods.refresh.call(this);
    }
  }
};

/**
 * Basic mixin for ol components that depends on map instance
 */
var useMapCmp = {
  methods: {
    /**
     * @return {Promise<void>}
     * @protected
     */
    beforeInit: function beforeInit() {
      var _context,
          _this = this;

      return (_context = (_context = (_context = (_context = interval$1.call(Observable$1, 100), skipWhile$1).call(_context, function () {
        return !_this.$map;
      }), first$1).call(_context), map$1).call(_context, function () {
        return _this;
      }), toPromise$1).call(_context, _Promise);
    }
  }
};

var props = {
  /**
   * Coordinates in the map view projection.
   * @type {number[]|ol.Coordinate}
   */
  coordinates: {
    type: Array,
    required: true,
    validator: function validator(val) {
      return val.length;
    }
  }
};

var computed = {
  /**
   * @type {string}
   * @abstract
   * @readonly
   */
  type: function type() {
    throw new Error('Not implemented computed property');
  },

  /**
   * @type {number[]|ol.Extent|undefined}
   */
  extent: function extent() {
    if (this.rev && this.$geometry && this.$view) {
      return this.extentToBindProj(this.$geometry.getExtent());
    }
  },

  /**
   * @type {number[]|ol.Coordinate|undefined}
   */
  pointOnSurface: function pointOnSurface$$1() {
    if (this.rev && this.$geometry && this.$view) {
      return this.pointToBindProj(pointOnSurface(this.$geometry));
    }
  },

  /**
   * @type {Array|undefined}
   */
  viewProjCoordinates: function viewProjCoordinates() {
    if (this.rev && this.$geometry) {
      return this.$geometry.getCoordinates();
    }
  }
};

var methods$1 = {
  /**
   * @return {ol.geometry.Geometry|Promise<ol.geometry.Geometry>}
   * @protected
   */
  createOlObject: function createOlObject() {
    return this.createGeometry();
  },

  /**
   * @return {ol.geometry.Geometry|Promise<ol.geometry.Geometry>}
   * @protected
   * @abstract
   */
  createGeometry: function createGeometry() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {Promise}
   * @throws {AssertionError}
   * @protected
   */
  init: function init() {
    hasView(this);
    // define helper methods based on geometry type
    var transform$$1 = transforms[this.type].transform;

    var geomProj = this.$view.getProjection();
    var bindProj = this.globOption('bindToProj', geomProj);
    /**
     * @method
     * @param {Array} coordinates
     * @return {number[]}
     * @protected
     */
    this.toBindProj = function (coordinates) {
      return transform$$1(coordinates, geomProj, bindProj);
    };
    /**
     * @method
     * @param {Array} coordinates
     * @return {number[]}
     * @protected
     */
    this.toViewProj = function (coordinates) {
      return transform$$1(coordinates, bindProj, geomProj);
    };

    return cmp.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return cmp.methods.deinit.call(this);
  },

  /**
   * @return {Promise}
   */
  refresh: function refresh() {
    return cmp.methods.refresh.call(this);
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return multiMergeDescriptors(cmp.methods.getServices.call(this), {
      get geometry() {
        return vm.$geometry;
      }
    });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$geometryContainer && this.$geometryContainer.setGeometry(this);
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();
    this.$geometryContainer && this.$geometryContainer.setGeometry(undefined);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToGeomChanges.call(this);
  }
};

var watch = {
  coordinates: function coordinates(value) {
    if (!this.$geometry || !this.$view) return;

    value = this.toViewProj(value);

    var isEq = isEqualGeom({
      coordinates: value,
      extent: boundingExtent(value)
    }, {
      coordinates: this.$geometry.getCoordinates(),
      extent: this.extent
    });

    if (!isEq) {
      this.$geometry.setCoordinates(value);
    }
  }
};

var geometry = {
  mixins: [cmp, useMapCmp, projTransforms],
  props: props,
  computed: computed,
  watch: watch,
  methods: methods$1,
  stubVNode: {
    empty: function empty() {
      return this.$options.name;
    }
  },
  data: function data() {
    return {
      rev: 1
    };
  },
  created: function created() {
    var _this = this;

    _Object$defineProperties(this, {
      /**
       * @type {ol.geom.Geometry|undefined}
       */
      $geometry: {
        enumerable: true,
        get: function get$$1() {
          return _this.$olObject;
        }
      },
      $map: {
        enumerable: true,
        get: function get$$1() {
          return _this.$services && _this.$services.map;
        }
      },
      $view: {
        enumerable: true,
        get: function get$$1() {
          return _this.$services && _this.$services.view;
        }
      },
      $geometryContainer: {
        enumerable: true,
        get: function get$$1() {
          return _this.$services && _this.$services.geometryContainer;
        }
      }
    });
  }
};

/**
 * @return {void}
 * @private
 */
function subscribeToGeomChanges() {
  var _context,
      _this2 = this;

  hasGeometry(this);

  var ft = 100;
  var changes = (_context = (_context = (_context = fromOlEvent(this.$geometry, 'change', function () {
    return {
      coordinates: _this2.toBindProj(_this2.$geometry.getCoordinates()),
      extent: _this2.extent
    };
  }), throttleTime$1).call(_context, ft), distinctUntilChanged$1).call(_context, isEqualGeom), map$1).call(_context, function (_ref) {
    var coordinates = _ref.coordinates;
    return {
      prop: 'coordinates',
      value: coordinates
    };
  });

  this.subscribeTo(changes, function (_ref2) {
    var prop = _ref2.prop,
        value = _ref2.value;

    ++_this2.rev;
    _this2.$emit('update:' + prop, value);
  });
}

/**
 * @param {{coordinates: number[], extent: number[]}} a
 * @param {{coordinates: number[], extent: number[]}} b
 * @returns {boolean}
 */
function isEqualGeom(a, b) {
  return _isEqual(a.extent, b.extent) ? _isEqual(a.coordinates, b.coordinates) : false;
}

var methods$3 = {
  /**
   * @return {{
   *     getGeometry: function(): ol.geom.Geometry|undefined,
   *     setGeometry: function(ol.geom.Geometry|undefined)
   *   }|undefined}
   * @protected
   */
  getGeometryTarget: function getGeometryTarget() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {ol.geom.Geometry|undefined}
   */
  getGeometry: function getGeometry() {
    return this._geometry;
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return {
      get geometryContainer() {
        return vm;
      }
    };
  },

  /**
   * @param {ol.geom.Geometry|Vue|GeoJSONGeometry|undefined} geom
   * @return {void}
   * @throws {AssertionError}
   */
  setGeometry: function setGeometry(geom) {
    if (geom instanceof Vue) {
      geom = geom.$geometry;
    } else if (_isPlainObject(geom)) {
      geom = this.readGeometryInBindProj(geom);
    }

    if (geom !== this._geometry) {
      this._geometry = geom;
    }

    var geomTarget = this.getGeometryTarget();
    if (geomTarget && geom !== geomTarget.getGeometry()) {
      geomTarget.setGeometry(geom);
    }
  }
};

var geometryContainer = {
  mixins: [projTransforms],
  methods: methods$3,
  created: function created() {
    /**
     * @type {ol.geom.Geometry|undefined}
     * @private
     */
    this._geometry = undefined;

    _Object$defineProperties(this, {
      $geometry: {
        enumerable: true,
        get: this.getGeometry
      }
    });
  },
  destroyed: function destroyed() {
    this._geometry = undefined;
  }
};

var props$3 = {
  attributions: [String, Array],
  logo: String,
  projection: String,
  wrapX: {
    type: Boolean,
    default: true
  }
};

var methods$5 = {
  /**
   * @return {ol.source.Source|Promise<ol.source.Source>}
   * @protected
   */
  createOlObject: function createOlObject() {
    return this.createSource();
  },

  /**
   * @return {ol.source.Source|Promise<ol.source.Source>}
   * @protected
   * @abstract
   */
  createSource: function createSource() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {Promise}
   * @protected
   */
  init: function init() {
    return cmp.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return cmp.methods.deinit.call(this);
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return multiMergeDescriptors(cmp.methods.getServices.call(this), {
      get source() {
        return vm.$source;
      }
    });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$sourceContainer && this.$sourceContainer.setSource(this);
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();
    this.$sourceContainer && this.$sourceContainer.setSource(undefined);
  },

  /**
   * @return {Promise}
   */
  refresh: function refresh() {
    return cmp.methods.refresh.call(this);
  }
};

var watch$1 = {
  attributions: function attributions(value) {
    this.$source && this.$source.setAttributions(value);
  }
};

var source = {
  mixins: [cmp, useMapCmp],
  props: props$3,
  methods: methods$5,
  watch: watch$1,
  stubVNode: {
    empty: function empty() {
      return this.$options.name;
    }
  },
  created: function created() {
    var _this = this;

    _Object$defineProperties(this, {
      /**
       * @type {ol.source.Source|undefined}
       */
      $source: {
        enumerable: true,
        get: function get() {
          return _this.$olObject;
        }
      },
      $map: {
        enumerable: true,
        get: function get() {
          return _this.$services && _this.$services.map;
        }
      },
      $view: {
        enumerable: true,
        get: function get() {
          return _this.$services && _this.$services.view;
        }
      },
      $sourceContainer: {
        enumerable: true,
        get: function get() {
          return _this.$services && _this.$services.sourceContainer;
        }
      }
    });
  }
};

/** @module core/image-source */
/**
 * @vueProps
 */
var props$2 = {
  crossOrigin: String,
  projection: {
    type: String,
    default: EPSG_3857
  }

  /**
   * @vueMethods
   */
};var methods$4 = {
  /**
   * @return {Promise}
   * @protected
   */
  init: function init() {
    return source.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return source.methods.deinit.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    source.methods.mount.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    source.methods.unmount.call(this);
  }
};

/**
 * @vueProto
 * @alias module:core/image-source
 */
var imageSource = {
  mixins: [source],
  props: props$2,
  methods: methods$4
};

/**
 * Basic style mixin.
 */
var methods$7 = {
  /**
   * @return {OlStyle|Promise<OlStyle>}
   * @protected
   */
  createOlObject: function createOlObject() {
    return this.createStyle();
  },

  /**
   * @return {OlStyle|Promise<OlStyle>}
   * @protected
   * @abstract
   */
  createStyle: function createStyle() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {Promise}
   * @protected
   */
  init: function init() {
    return cmp.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return cmp.methods.deinit.call(this);
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return multiMergeDescriptors(cmp.methods.getServices.call(this), {
      get style() {
        return vm.$style;
      }
    });
  },

  /**
   * @return {Promise}
   */
  refresh: function refresh() {
    return _Promise.resolve(this.unmount()).then(this.mount);
  }
};

var style$2 = {
  mixins: [cmp, useMapCmp],
  methods: methods$7,
  stubVNode: {
    empty: function empty() {
      return this.$options.name;
    }
  },
  created: function created() {
    var _this = this;

    _Object$defineProperties(this, {
      $style: {
        enumerable: true,
        get: function get() {
          return _this.$olObject;
        }
      },
      $map: {
        enumerable: true,
        get: function get() {
          return _this.$services && _this.$services.map;
        }
      },
      $view: {
        enumerable: true,
        get: function get() {
          return _this.$services && _this.$services.view;
        }
      },
      $stylesContainer: {
        enumerable: true,
        get: function get() {
          return _this.$services && _this.$services.stylesContainer;
        }
      }
    });
  }
};

var props$4 = {
  snapToPixel: {
    type: Boolean,
    default: true
  }
};

var methods$6 = {
  /**
   * @return {Promise}
   * @protected
   */
  init: function init() {
    return style$2.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return style$2.methods.deinit.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$stylesContainer && this.$stylesContainer.setImage(this);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.$stylesContainer && this.$stylesContainer.setImage(undefined);
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    return style$2.methods.getServices.call(this);
  },

  /**
   * @return {Promise}
   */
  refresh: function refresh() {
    // recreate style
    return _Promise.resolve(this.init()).then(this.mount);
  }
};

var watch$2 = {
  snapToPixel: function snapToPixel() {
    this.refresh();
  }
};

var imageStyle = {
  mixins: [style$2],
  props: props$4,
  methods: methods$6,
  watch: watch$2,
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        class: this.$options.name
      };
    }
  }
};

var props$5 = {
  id: {
    type: [String, Number],
    default: function _default() {
      return uuid();
    }
  },
  active: {
    type: Boolean,
    default: true
  }
};

var methods$8 = {
  /**
   * @return {Promise<ol.interaction.Interaction>}
   * @protected
   */
  createOlObject: function createOlObject() {
    var _this = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var interaction;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return _this.createInteraction();

            case 2:
              interaction = _context.sent;

              interaction.setActive(_this.active);
              interaction.set('id', _this.id);

              return _context.abrupt('return', interaction);

            case 6:
            case 'end':
              return _context.stop();
          }
        }
      }, _callee, _this);
    }))();
  },

  /**
   * @return {ol.interaction.Interaction|Promise<ol.interaction.Interaction>}
   * @protected
   * @abstract
   */
  createInteraction: function createInteraction() {
    throw new Error('Not implemented method');
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return multiMergeDescriptors(cmp.methods.getServices.call(this), {
      get interaction() {
        return vm.$interaction;
      }
    });
  },

  /**
   * @return {Promise} Resolves when initialization completes
   * @protected
   */
  init: function init() {
    return cmp.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return cmp.methods.deinit.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$interactionsContainer && this.$interactionsContainer.addInteraction(this);
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();
    this.$interactionsContainer && this.$interactionsContainer.removeInteraction(this);
  },

  /**
   * @return {Promise}
   */
  refresh: function refresh() {
    return cmp.methods.refresh.call(this);
  }
};

var watch$3 = {
  active: function active(value) {
    if (this.$interaction && value !== this.$interaction.getActive()) {
      this.$interaction.setActive(value);
    }
  }
};

var interaction = {
  mixins: [cmp, useMapCmp],
  props: props$5,
  methods: methods$8,
  watch: watch$3,
  stubVNode: {
    empty: function empty() {
      return this.$options.name;
    }
  },
  created: function created() {
    var _this2 = this;

    _Object$defineProperties(this, {
      /**
       * @type {ol.interaction.Interaction|undefined}
       */
      $interaction: {
        enumerable: true,
        get: function get() {
          return _this2.$olObject;
        }
      },
      $map: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.map;
        }
      },
      $view: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.view;
        }
      },
      $interactionsContainer: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.interactionsContainer;
        }
      }
    });
  }
};

var methods$9 = {
  /**
   * @return {{
   *     hasInteraction: function(ol.interaction.Interaction): bool,
   *     addInteraction: function(ol.interaction.Interaction): void,
   *     removeInteraction: function(ol.interaction.Interaction): void
   *   }|undefined}
   * @protected
   */
  getInteractionsTarget: function getInteractionsTarget() {
    throw new Error('Not implemented method');
  },

  /**
   * @param {ol.interaction.Interaction|Vue} interaction
   * @return {void}
   */
  addInteraction: function addInteraction(interaction) {
    interaction = interaction instanceof Vue ? interaction.$interaction : interaction;
    instanceOf(interaction, Interaction);

    if (!this._interactions[interaction.get('id')]) {
      this._interactions[interaction.get('id')] = interaction;
    }

    var interactionsTarget = this.getInteractionsTarget();
    if (interactionsTarget && !interactionsTarget.hasInteraction(interaction)) {
      interactionsTarget.addInteraction(interaction);
    }
  },

  /**
   * @param {ol.interaction.Interaction|Vue} interaction
   * @return {void}
   */
  removeInteraction: function removeInteraction(interaction) {
    interaction = interaction instanceof Vue ? interaction.$interaction : interaction;

    if (!interaction) return;

    delete this._interactions[interaction.get('id')];

    var interactionsTarget = this.getInteractionsTarget();
    if (interactionsTarget && interactionsTarget.hasInteraction(interaction)) {
      interactionsTarget.removeInteraction(interaction);
    }
  },

  /**
   * @return {ol.interaction.Interaction[]}
   */
  getInteractions: function getInteractions() {
    return _Object$values(this._interactions);
  },

  /**
   * @param {string|number} id
   * @return {ol.interaction.Interaction|undefined}
   */
  getInteractionById: function getInteractionById(id) {
    return this._interactions[id];
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return {
      get interactionsContainer() {
        return vm;
      }
    };
  }
};

var interactionsContainer = {
  methods: methods$9,
  created: function created() {
    /**
     * @type {Object<string, ol.interaction.Interaction>}
     * @private
     */
    this._interactions = _Object$create(null);
  },
  destroyed: function destroyed() {
    this._interactions = _Object$create(null);
  }
};

var methods$11 = {
  /**
   * @return {{
   *     setSource: function(ol.source.Source): void,
   *     getSource: function(): ol.source.Source
   *   }|undefined}
   * @protected
   */
  getSourceTarget: function getSourceTarget() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {ol.source.Source|undefined}
   */
  getSource: function getSource() {
    return this._source;
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return {
      get sourceContainer() {
        return vm;
      }
    };
  },

  /**
   * @param {ol.source.Source|Vue|undefined} source
   * @return {void}
   */
  setSource: function setSource(source) {
    source = source instanceof Vue ? source.$source : source;

    if (source !== this._source) {
      this._source = source;
    }

    var sourceTarget = this.getSourceTarget();
    if (sourceTarget && source !== sourceTarget.getSource()) {
      sourceTarget.setSource(source);
    }
  }
};

var sourceContainer = {
  methods: methods$11,
  created: function created() {
    /**
     * @type {ol.source.Source|undefined}
     * @private
     */
    this._source = undefined;
  },
  destroyed: function destroyed() {
    this._source = undefined;
  }
};

/**
 * @module core/mixins/layer
 */
/**
 * @vueProps
 */
var props$6 = /** @lends module:core/mixins/layer# */{
  id: {
    type: [String, Number],
    default: function _default() {
      return uuid();
    }
  },
  /**
   * The bounding extent for layer rendering defined in the map view projection.
   * The layer will not be rendered outside of this extent.
   * @default undefined
   * @type {ol.Extent|number[]|undefined}
   */
  extent: {
    type: Array,
    validator: function validator(value) {
      return value.length === 4;
    }
  },
  minResolution: Number,
  maxResolution: Number,
  opacity: {
    type: Number,
    default: 1
  },
  overlay: Boolean,
  visible: {
    type: Boolean,
    default: true
  },
  zIndex: Number
};

var methods$10 = {
  /**
   * @return {Promise<ol.layer.Layer>}
   * @protected
   */
  createOlObject: function createOlObject() {
    var _this = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var layer;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return _this.createLayer();

            case 2:
              layer = _context.sent;

              layer.set('id', _this.id);

              return _context.abrupt('return', layer);

            case 5:
            case 'end':
              return _context.stop();
          }
        }
      }, _callee, _this);
    }))();
  },

  /**
   * @return {ol.layer.Layer|Promise<ol.layer.Layer>}
   * @protected
   * @abstract
   */
  createLayer: function createLayer() {
    throw new Error('Not implemented method');
  },

  /**
   * @return {Promise<Vue<ol.layer.Layer>>}
   * @protected
   */
  init: function init() {
    return cmp.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return cmp.methods.deinit.call(this);
  },

  /**
   * @param {number[]} pixel
   * @return {boolean}
   */
  isAtPixel: function isAtPixel(pixel) {
    var _this2 = this;

    hasMap(this);

    return this.$map.forEachLayerAtPixel(pixel, function (l) {
      return l === _this2.$layer;
    });
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return multiMergeDescriptors(cmp.methods.getServices.call(this), sourceContainer.methods.getServices.call(this), {
      get layer() {
        return vm.$layer;
      }
    });
  },

  /**
   * @return {{
   *     setSource: function(ol.source.Source): void,
   *     getSource: function(): ol.source.Source
   *   }|undefined}
   * @protected
   */
  getSourceTarget: function getSourceTarget() {
    return this.$layer;
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    if (this.overlay && this.$map) {
      this.setMap(this.$map);
    } else if (this.$layersContainer) {
      this.$layersContainer.addLayer(this);
    }

    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();

    if (this.overlay) {
      this.setMap(undefined);
    } else if (this.$layersContainer) {
      this.$layersContainer.removeLayer(this);
    }
  },

  /**
   * Updates layer state
   * @return {Promise}
   */
  refresh: function refresh() {
    return cmp.methods.refresh.call(this);
  },

  /**
   * @param {ol.Map|Vue|undefined} map
   */
  setMap: function setMap(map$$1) {
    hasLayer(this);

    map$$1 = map$$1 instanceof Vue ? map$$1.$map : map$$1;
    this.$layer.setMap(map$$1);
  }
};

var watch$4 = {
  id: function id(value) {
    if (this.$layer && value !== this.$layer.get('id')) {
      this.$layer.set('id', value);
    }
  },
  maxResolution: function maxResolution(value) {
    if (this.$layer && value !== this.$layer.getMaxResolution()) {
      this.$layer.setMaxResolution(value);
    }
  },
  minResolution: function minResolution(value) {
    if (this.$layer && value !== this.$layer.getMinResolution()) {
      this.$layer.setMinResolution(value);
    }
  },
  opacity: function opacity(value) {
    if (this.$layer && value !== this.$layer.getOpacity()) {
      this.$layer.setOpacity(value);
    }
  },
  visible: function visible(value) {
    if (this.$layer && value !== this.$layer.getVisible()) {
      this.$layer.setVisible(value);
    }
  },
  zIndex: function zIndex(value) {
    if (this.$layer && value !== this.$layer.getZIndex()) {
      this.$layer.setZIndex(value);
    }
  }
};

var layer = {
  mixins: [cmp, useMapCmp, sourceContainer],
  props: props$6,
  methods: methods$10,
  watch: watch$4,
  stubVNode: {
    attrs: function attrs() {
      return {
        id: [this.$options.name, this.id].join('-'),
        class: this.$options.name
      };
    }
  },
  created: function created() {
    var _this3 = this;

    _Object$defineProperties(this, {
      /**
       * @type {ol.layer.Layer|undefined}
       */
      $layer: {
        enumerable: true,
        get: function get() {
          return _this3.$olObject;
        }
      },
      $map: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.map;
        }
      },
      $view: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.view;
        }
      },
      $layersContainer: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.layersContainer;
        }
      }
    });
  }
};

var methods$12 = {
  /**
   * @return {{
   *     hasLayer: function(ol.Layer): bool,
   *     addLayer: function(ol.Layer): void,
   *     removeLayer: function(ol.Layer): void
   *   }|undefined}
   * @protected
   */
  getLayersTarget: function getLayersTarget() {
    throw new Error('Not implemented method');
  },

  /**
   * @param {ol.layer.Layer|Vue} layer
   * @return {void}
   */
  addLayer: function addLayer(layer) {
    layer = layer instanceof Vue ? layer.$layer : layer;
    instanceOf(layer, Layer);

    if (!this._layers[layer.get('id')]) {
      this._layers[layer.get('id')] = layer;
    }

    var layersTarget = this.getLayersTarget();
    if (layersTarget && !layersTarget.hasLayer(layer)) {
      layersTarget.addLayer(layer);
    }
  },

  /**
   * @param {ol.layer.Layer|Vue} layer
   * @return {void}
   */
  removeLayer: function removeLayer(layer) {
    layer = layer instanceof Vue ? layer.$layer : layer;

    if (!layer) return;

    delete this._layers[layer.get('id')];

    var layersTarget = this.getLayersTarget();
    if (layersTarget && layersTarget.hasLayer(layer)) {
      layersTarget.removeLayer(layer);
    }
  },

  /**
   * @return {ol.layer.Layer[]}
   */
  getLayers: function getLayers() {
    return _Object$values(this._layers);
  },

  /**
   * @param {string|number} id
   * @return {ol.layer.Layer|undefined}
   */
  getLayerById: function getLayerById(id) {
    return this._layers[id];
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return {
      get layersContainer() {
        return vm;
      }
    };
  }
};

var layersContainer = {
  methods: methods$12,
  created: function created() {
    /**
     * @type {Object<string, ol.layer.Layer>}
     * @private
     */
    this._layers = _Object$create(null);
  },
  destroyed: function destroyed() {
    this._layers = _Object$create(null);
  }
};

var methods$13 = {
  /**
   * @return {{
   *     hasOverlay: function(ol.Overlay): bool,
   *     addOverlay: function(ol.Overlay): void,
   *     removeOverlay: function(ol.Overlay): void
   *   }|undefined}
   * @protected
   */
  getOverlaysTarget: function getOverlaysTarget() {
    throw new Error('Not implemented method');
  },

  /**
   * @param {ol.Overlay|Vue} overlay
   * @return {void}
   */
  addOverlay: function addOverlay(overlay) {
    overlay = overlay instanceof Vue ? overlay.$overlay : overlay;
    instanceOf(overlay, Overlay);

    if (!this._overlays[overlay.getId()]) {
      this._overlays[overlay.getId()] = overlay;
    }

    var overlaysTarget = this.getOverlaysTarget();
    if (overlaysTarget && !overlaysTarget.hasOverlay(overlay)) {
      overlaysTarget.addOverlay(overlay);
    }
  },

  /**
   * @param {ol.Overlay|Vue} overlay
   * @return {void}
   */
  removeOverlay: function removeOverlay(overlay) {
    overlay = overlay instanceof Vue ? overlay.$overlay : overlay;

    if (!overlay) return;

    delete this._overlays[overlay.getId()];

    var overlaysTarget = this.getOverlaysTarget();
    if (overlaysTarget && overlaysTarget.hasOverlay(overlay)) {
      overlaysTarget.removeOverlay(overlay);
    }
  },

  /**
   * @return {ol.Overlay[]}
   */
  getOverlays: function getOverlays() {
    return _Object$values(this._overlays);
  },

  /**
   * @param {string|number} id
   * @return {ol.Overlay|undefined}
   */
  getOverlayById: function getOverlayById(id) {
    return this._overlays[id];
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return {
      get overlaysContainer() {
        return vm;
      }
    };
  }
};

var overlaysContainer = {
  methods: methods$13,
  created: function created() {
    /**
     * @type {Object<string, ol.Overlay>}
     * @private
     */
    this._overlays = _Object$create(null);
  },
  destroyed: function destroyed() {
    this._overlays = _Object$create(null);
  }
};

var stylesContainer = {
  created: function created() {
    /**
     * @type {ol.style.Style[]|ol.StyleFunction|Vue|undefined}
     * @private
     */
    this._styles = undefined;
  },
  destroyed: function destroyed() {
    this._styles = undefined;
  },

  methods: {
    /**
     * Default style factory
     * @return {ol.style.Style[]|ol.StyleFunction|undefined}
     * @protected
     */
    getDefaultStyles: function getDefaultStyles() {},

    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;

      return {
        get stylesContainer() {
          return vm;
        }
      };
    },

    /**
     * @return {ol.style.Style[]|ol.StyleFunction|Vue|undefined}
     */
    getStyles: function getStyles() {
      return this._styles;
    },

    /**
     * @param {ol.style.Style|ol.StyleFunction|Vue|undefined} style
     * @return {void}
     */
    addStyle: function addStyle(style) {
      var currentStyles = this.getStyles();
      var olStyle = style instanceof Vue ? style.$style : style;

      if (_isFunction(olStyle)) {
        if (currentStyles) {
          warndbg('Component already has style components among it\'s descendants. ' + 'Avoid use of multiple vl-style-func or combining vl-style-func with vl-style-box on the same level');
        }
        currentStyles = style;
      } else {
        if (!Array.isArray(currentStyles)) {
          if (currentStyles) {
            warndbg('Component already has style components among it\'s descendants. ' + 'Avoid use of multiple vl-style-func or combining vl-style-func with vl-style-box on the same level');
          }
          currentStyles = [];
        }
        style = style instanceof Vue ? style : { $style: style, condition: true };
        if (!currentStyles.includes(style)) {
          currentStyles.push(style);
        }
      }

      this.setStyle(currentStyles);
    },

    /**
     * @param {Array<{style: ol.style.Style, condition: (function|boolean|undefined)}>|ol.StyleFunction|Vue|undefined} styles
     * @return {void}
     */
    setStyle: function setStyle(styles) {
      if (styles === this._styles) return;
      this._styles = styles;

      var styleTarget = this.getStyleTarget();
      if (!styleTarget) return;

      if (this._styles === null || this._styles) {
        styleTarget.setStyle(this.createStyleFunc());
      } else {
        styleTarget.setStyle(undefined);
      }
    },

    /**
     * @param {ol.style.Style|ol.StyleFunction|Vue|undefined} style
     * @return {void}
     */
    removeStyle: function removeStyle(style) {
      var currentStyles = this.getStyles();

      if (currentStyles === style) {
        currentStyles = undefined;
      } else if (Array.isArray(currentStyles)) {
        currentStyles = currentStyles.filter(function (s) {
          return style instanceof Vue ? s !== style : s.$style !== style;
        });
        currentStyles.length || (currentStyles = undefined);
      }

      this.setStyle(currentStyles);
    },

    /**
     * Returns OL object that can be styled (i.e. has setStyle/getStyle methods) or undefined
     * @return {*}
     * @protected
     * @abstract
     */
    getStyleTarget: function getStyleTarget() {
      throw new Error('Not implemented method');
    },

    /**
     * Style function factory
     * @returns {ol.StyleFunction}
     * @protected
     */
    createStyleFunc: function createStyleFunc() {
      var vm = this;
      var defaultStyles = this.getDefaultStyles();

      return function __styleTargetStyleFunc(feature, resolution) {
        if (!feature.getGeometry()) return;
        var styles = vm.getStyles();
        /* eslint-disable brace-style */
        // handle provided styles
        // styles - ol.StyleFunction or vl-style-func
        if (styles && (_isFunction(styles) || _isFunction(styles.$style))) {
          var styleFunc = _isFunction(styles) ? styles : styles.$style;
          styles = styleFunc(feature, resolution);
        }
        // styles is array of { $style: ol.style.Style, condition: (bool|function():bool) }
        else if (Array.isArray(styles)) {
            styles = _flow(_filter(function (_ref) {
              var condition = _ref.condition;

              return condition == null || condition === true || _isFunction(condition) && condition(feature, resolution);
            }), _map('$style'))(styles);
          }
        /* eslint-enable brace-style */
        // not empty or null style
        if (styles === null || Array.isArray(styles) && styles.length) {
          return styles;
        }
        // fallback to default style
        styles = defaultStyles;
        if (styles) {
          return _isFunction(styles) ? styles(feature, resolution) : styles;
        }
      };
    }
  }
};

/**
 * Replaces `tokens` in the `string` by values from the `replaces`.
 *
 * @param {string} string
 * @param {Object} replaces
 *
 * @returns {string}
 */
function replaceTokens(string, replaces) {
  var regExp = new RegExp(_Object$keys(replaces).map(function (field) {
    return '(\\{' + field + '\\})';
  }).join('|'), 'ig');

  return string.replace(regExp, function (match) {
    return replaces[match.substr(1, match.length - 2)] || '';
  });
}

var withUrl = {
  props: {
    url: {
      type: String
    }
  },
  computed: {
    /**
     * @type {string}
     */
    urlTmpl: function urlTmpl() {
      return replaceTokens(this.url, _pick(this.urlTokens, this));
    },

    /**
     * @type {string[]}
     */
    urlTokens: function urlTokens() {
      return [];
    }
  }
};

var props$7 = {
  cacheSize: {
    type: Number,
    default: CACHE_SIZE
  },
  crossOrigin: String,
  maxZoom: {
    type: Number,
    default: MAX_ZOOM
  },
  minZoom: {
    type: Number,
    default: MIN_ZOOM
  },
  opaque: Boolean,
  projection: {
    type: String,
    default: EPSG_3857
  },
  reprojectionErrorThreshold: {
    type: Number,
    default: REPROJ_ERR_THRESHOLD
  },
  tilePixelRatio: {
    type: Number,
    default: PIXEL_RATIO
  },
  tileSize: {
    type: Array,
    default: function _default() {
      return [TILE_SIZE, TILE_SIZE];
    },
    validator: function validator(value) {
      return value.length === 2;
    }
  },
  url: {
    type: [String, Function],
    required: true
  }
};

var computed$1 = {
  /**
   * @type {string}
   */
  urlTmpl: function urlTmpl() {
    if (!_isString(this.url)) {
      return '';
    }
    return replaceTokens(this.url, _pick(this.urlTokens, this));
  }
};

var methods$14 = {
  /**
   * @return {ol.tilegrid.TileGrid}
   * @protected
   */
  createTileGrid: function createTileGrid() {
    hasView(this);

    return createXYZ({
      extent: fromProjection(this.$view.getProjection()),
      maxZoom: this.maxZoom,
      minZoom: this.minZoom,
      tileSize: this.tileSize
    });
  },

  /**
   * @return {ol.TileUrlFunctionType}
   * @protected
   */
  createUrlFunc: function createUrlFunc() {
    if (_isFunction(this.url)) {
      return this.url;
    }
    return _constant(this.urlTmpl);
  },

  /**
   * @return {Promise}
   * @protected
   */
  init: function init() {
    /**
     * @type {ol.tilegrid.TileGrid}
     * @protected
     */
    this._tileGrid = this.createTileGrid();

    return source.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    this._tileGrid = undefined;

    return source.methods.deinit.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    source.methods.mount.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    source.methods.mount.call(this);
  }
};

var watch$5 = {
  url: function url() {
    if (this.$source) {
      this.$source.setTileUrlFunction(this.createUrlFunc());
    }
  }
};

var tileSource = {
  mixins: [source, withUrl],
  props: props$7,
  computed: computed$1,
  methods: methods$14,
  watch: watch$5
};

var props$8 = {
  /**
   * Source data projection.
   * @type {string}
   * @default EPSG:4326
   */
  projection: {
    type: String,
    default: EPSG_4326
  },
  useSpatialIndex: {
    type: Boolean,
    default: true
  }
};

var computed$2 = {
  viewProjFeatures: function viewProjFeatures() {
    if (this.rev && this.$source) {
      return this.getFeatures().map(function (feature) {
        return writeFeature(feature);
      });
    }
    return [];
  }
};

var methods$15 = {
  /**
   * @return {void}
   */
  clear: function clear() {
    featuresContainer.methods.clear.call(this);
    this.$source && this.$source.clear();
  },

  /**
   * @return {{
   *     addFeature: function(ol.Feature): void,
   *     removeFeature: function(ol.Feature): void,
   *     hasFeature: function(ol.Feature): bool
   *   }|undefined}
   * @protected
   */
  getFeaturesTarget: function getFeaturesTarget() {
    if (!this.$source) return;

    var source$$1 = this.$source;

    return {
      hasFeature: function hasFeature$$1(feature) {
        return source$$1.getFeatureById(feature.getId()) != null;
      },
      addFeature: function addFeature(feature) {
        source$$1.addFeature(feature);
      },
      removeFeature: function removeFeature(feature) {
        source$$1.removeFeature(feature);
      }
    };
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    return multiMergeDescriptors(source.methods.getServices.call(this), featuresContainer.methods.getServices.call(this));
  },

  /**
   * @return {Promise}
   * @protected
   */
  init: function init() {
    return source.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    return source.methods.deinit.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    source.methods.mount.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    source.methods.unmount.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToSourceChanges.call(this);
  }
};

var vectorSource = {
  mixins: [source, featuresContainer, projTransforms],
  props: props$8,
  computed: computed$2,
  methods: methods$15,
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        class: this.$options.name
      };
    }
  }
};

function subscribeToSourceChanges() {
  var _context,
      _this = this;

  hasSource(this);

  var add = (_context = fromOlEvent(this.$source, 'addfeature'), _do$1).call(_context, function (_ref) {
    var feature = _ref.feature;

    _this.addFeature(feature);
  });
  var remove = (_context = fromOlEvent(this.$source, 'removefeature'), _do$1).call(_context, function (_ref2) {
    var feature = _ref2.feature;

    _this.removeFeature(feature);
  });

  var events = merge$1.call(Observable$1, add, remove);

  this.subscribeTo(events, function (evt) {
    return _this.$emit(evt.type, evt);
  });
  // emit event to allow `sync` modifier
  this.subscribeTo(debounceTime$1.call(events, 100), function () {
    ++_this.rev;
    _this.$emit('update:features', _this.getFeatures().map(_this.writeFeatureInBindProj.bind(_this)));
  });
}

var methods$16 = {
  /**
   * @param {ol.style.Fill|Vue|undefined} fill
   * @return {void}
   * @protected
   */
  setFill: function setFill(fill) {
    fill = fill instanceof Vue ? fill.$style : fill;

    if (fill !== this._fill) {
      this._fill = fill;
    }
    if (this.$style && fill !== this.$style.getFill()) {
      this.$style.setFill(fill);
      this.refresh();
    }
  },

  /**
   * @param {ol.style.Stroke|Vue|undefined} stroke
   * @return {void}
   * @protected
   */
  setStroke: function setStroke(stroke) {
    stroke = stroke instanceof Vue ? stroke.$style : stroke;

    if (stroke !== this._stroke) {
      this._stroke = stroke;
    }
    if (this.$style && stroke !== this.$style.getStroke()) {
      this.$style.setStroke(stroke);
      this.refresh();
    }
  }
};

var withFillStrokeStyle = {
  methods: methods$16,
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        class: this.$options.name
      };
    }
  },
  created: function created() {
    /**
     * @type {ol.style.Fill|undefined}
     * @private
     */
    this._fill = undefined;
    /**
     * @type {ol.style.Stroke|undefined}
     * @private
     */
    this._stroke = undefined;
  },
  destroyed: function destroyed() {
    this._fill = this._stroke = undefined;
  }
};

var methods$17 = {
  /**
   * @return {ol.source.XYZ}
   * @protected
   */
  createSource: function createSource() {
    return new XYZSource({
      attributions: this.attributions,
      cacheSize: this.cacheSize,
      crossOrigin: this.crossOrigin,
      maxZoom: this.maxZoom,
      minZoom: this.minZoom,
      opaque: this.opaque,
      projection: this.projection,
      reprojectionErrorThreshold: this.reprojectionErrorThreshold,
      tileGrid: this._tileGrid,
      tilePixelRatio: this.tilePixelRatio,
      tileUrlFunction: this.createUrlFunc(),
      wrapX: this.wrapX
    });
  },

  /**
   * @return {ol.TileUrlFunction}
   * @protected
   */
  createUrlFunc: function createUrlFunc() {
    // custom url function provided
    if (_isFunction(this.url)) {
      return this.url;
    }
    hasView(this);
    // or use url function from ol-tilecache
    return createTileUrlFunction(this.urlTmpl, this._tileGrid, fromProjection(this.$view.getProjection()));
  }
};

var watch$6 = {};

var xyzSource = {
  mixins: [tileSource],
  methods: methods$17,
  watch: watch$6
};

/**
 * @param {...*} [args]
 *
 * @return {*}
 */
function coalesce() {
  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return args.find(function (val) {
    return val != null;
  });
}

/**
 * @func
 * @param {Object} value
 * @param {Object} Returns object only with plain properties.
 */
var plainProps = _pickBy(function (x) {
  return _isNumber(x) || _isString(x) || Array.isArray(x) || _isBoolean(x) || _isPlainObject(x);
});

/**
 * VueLayers Core
 * @module core
 */
// rx helpers
// ol helpers

// mixins

// utils

export { fromOlEvent as observableFromOlEvent, fromOlChangeEvent as observableFromOlChangeEvent, extent as extentHelper, geojson as geoJsonHelper, geomHelper, proj as projHelper, style$1 as styleHelper, tileGrid as tileGridHelper, loadStrategy as loadStrategyHelper, format as formatHelper, feature as featureHelper, easing as easingHelper, featuresContainer, geometry, geometryContainer, identMap, imageSource, imageStyle, interaction, interactionsContainer, layer, layersContainer, cmp$1 as olCmp, cmp as olVirtCmp, overlaysContainer, projTransforms, rxSubs, services, source, sourceContainer, stubVNode, style$2 as style, stylesContainer, tileSource, useMapCmp, vectorSource, withFillStrokeStyle, withUrl, xyzSource, assert$1 as assert, coalesce, log$1 as log, IdentityMap, isNumeric, multiMergeDescriptors as mergeDescriptors, plainProps, replaceTokens, SERVICES_PROP, VM_PROP, IDENTITY_MAP_PROP, VL_OPTIONS, EPSG_4326, EPSG_3857, MAX_ZOOM, MIN_ZOOM, TILE_SIZE, TILE_FORMAT, ZOOM_FACTOR, CACHE_SIZE, PIXEL_RATIO, CROSS_ORIGIN, REPROJ_ERR_THRESHOLD, EARTH_RADIUS, WGS84_SPHERE, RENDERER_TYPE, GEOMETRY_TYPE, EXTENT_CORNER, PROJ_UNIT, OVERLAY_POSITIONING, WMS_VERSION, WMTS_VERSION, WMTS_REQUEST_ENCODING, WMTS_FORMAT, LAYER_PROP };

//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/vuelayers/lib/core/index.es.js
// module id = null
// module chunks = 